import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Module-level "current team" that useTeamId pushes to whenever it
// resolves. Lets the request interceptor below auto-attach teamId to
// outbound requests without every call site having to thread it through.
// Backed up to localStorage so a hard refresh doesn't lose it before
// useTeamId remounts.
let currentTeamId: string | null = null;
export function setCurrentTeamId(teamId: string | null) {
  currentTeamId = teamId;
  if (teamId) localStorage.setItem('clarid:current-team-id', teamId);
  else localStorage.removeItem('clarid:current-team-id');
}
function getCurrentTeamId(): string | null {
  if (currentTeamId) return currentTeamId;
  try {
    const cached = localStorage.getItem('clarid:current-team-id');
    if (cached) return cached;
    // Fallback to Zustand-persisted active team in case the setter
    // hasn't run yet (e.g. very first request after a hard refresh,
    // before useTeamId has resolved).
    const raw = localStorage.getItem('active-team-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.state?.activeTeamId ?? null;
    }
  } catch {
    /* ignore — corrupt localStorage shouldn't break requests */
  }
  return null;
}

// Request interceptor:
//  1. Attach bearer token for authenticated calls.
//  2. Auto-inject teamId on routes that don't carry it in the URL. The
//     /fair-lending/*, /hmda/*, /lar-builder/*, /regression/*, /apor/*,
//     /edit-intelligence/*, /geocoding-validation/*, and /loan-officer/*
//     namespaces all use the resolveTeam middleware, which since
//     2026-05-21 fails with 400 team_id_required for multi-team users
//     who don't pass an explicit teamId. Routes that DO carry teamId in
//     the URL (everything under /teams/...) are left alone so we don't
//     create redundant params or override URL-path semantics.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (token) {
    const url = config.url ?? '';
    const alreadyHasTeamScope =
      url.startsWith('/teams/') ||
      url.startsWith('teams/') ||
      (config.params && typeof config.params === 'object' && 'teamId' in config.params);
    if (!alreadyHasTeamScope) {
      const tid = getCurrentTeamId();
      if (tid) {
        config.params = { ...(config.params || {}), teamId: tid };
      }
    }
  }
  return config;
});

// Endpoints whose errors should NEVER trigger a global toast.
// These are either:
//   - Polling/background queries (silent failure expected)
//   - Permission probes (the calling component needs the rejection silently)
//   - Endpoints with their own custom error handling that shows inline UI
const SILENT_ERROR_PATHS: RegExp[] = [
  /\/auth\/me$/,
  /\/users\/me$/,
  /\/teams\/[^/]+\/me$/,           // RBAC role probe
  /\/teams\/[^/]+\/check-permission$/,
  /\/teams$/,                       // initial team load
  /\/dashboard\/health$/,           // dashboard polling
  /\/notifications/,
  /\/feature-gates\/check/,
  /\/audit-logs\/verify-integrity$/,// has its own UI banner
];

function shouldToastError(error: any): boolean {
  const url: string = error.config?.url || '';
  return !SILENT_ERROR_PATHS.some((pattern) => pattern.test(url));
}

/**
 * Extract a human-readable error message from an axios error response.
 *
 * Preference order:
 *   1. error.response.data.error (string) — standard backend envelope
 *   2. error.response.data.message — alternate envelope from older routes
 *   3. Lookup by error.response.data.code — friendly mapping for stable
 *      machine codes (permission_denied, not_team_member, etc.)
 *   4. Lookup by HTTP status — generic fallback
 *   5. Network error / unknown
 */
const CODE_MESSAGES: Record<string, string> = {
  not_authenticated: 'Please log in to continue.',
  permission_denied: "You don't have permission to perform this action.",
  not_team_member: "You're not a member of this team.",
  team_id_required: 'Please select a team first.',
  not_found: 'The requested resource was not found.',
  validation_failed: 'Please check the form and try again.',
  subscription_required: 'An active subscription is required for this feature.',
  conflict: 'This action conflicts with the current state.',
};

/**
 * Extract field-level validation errors from a backend error response so
 * forms can render them inline next to the offending input. Returns a
 * { [fieldPath]: errorMessage } map, or an empty object if the error
 * isn't a validation error.
 *
 * Example usage in a form:
 *   try {
 *     await api.post('/foo', data);
 *   } catch (err) {
 *     const fieldErrors = extractFieldErrors(err);
 *     if (fieldErrors.email) setEmailError(fieldErrors.email);
 *   }
 */
export function extractFieldErrors(error: any): Record<string, string> {
  const data = error?.response?.data;
  if (data?.code !== 'validation_failed' || !Array.isArray(data?.fieldErrors)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const fe of data.fieldErrors) {
    if (fe?.path && fe?.message) out[fe.path] = fe.message;
  }
  return out;
}

function getErrorMessage(error: any): string {
  const data = error.response?.data;
  if (typeof data === 'string') return data;
  if (data?.error && typeof data.error === 'string') return data.error;
  if (data?.message && typeof data.message === 'string') return data.message;
  if (data?.code && CODE_MESSAGES[data.code]) return CODE_MESSAGES[data.code];

  const status = error.response?.status;
  switch (status) {
    case 400: return 'Invalid request. Please check the form and try again.';
    case 403: return "You don't have permission to perform this action.";
    case 404: return 'The requested resource was not found.';
    case 409: return 'Conflict — this action conflicts with the current state.';
    case 422: return 'Validation error. Please check the form and try again.';
    case 429: return 'Too many requests. Please slow down and try again.';
    case 500: return 'Server error. Our team has been notified.';
    case 502:
    case 503:
    case 504: return 'Service temporarily unavailable. Please try again in a moment.';
    default:
      if (!error.response) return 'Network error. Check your connection and try again.';
      return `Request failed (HTTP ${status}).`;
  }
}

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const currentPath = window.location.pathname;

    // 401 — session expired or invalid token
    if (status === 401) {
      localStorage.removeItem('token');
      setCurrentTeamId(null);
      if (currentPath !== '/login' && currentPath !== '/register' && currentPath !== '/') {
        // QA #4 #A: previously called toast.error here but window.location.href
        // does a full page reload that destroys the toast container before it
        // renders. Pass the reason via query param so LoginPage can show it
        // on mount and the user understands why they were kicked out.
        window.location.href = '/login?reason=session-expired';
      }
      return Promise.reject(error);
    }

    // 403 subscription_required — redirect TRIAL users to billing
    if (status === 403 && error.response?.data?.error === 'subscription_required') {
      if (currentPath !== '/billing') {
        toast.error('An active subscription is required. Please subscribe to access this feature.');
        window.location.href = '/billing';
      }
      return Promise.reject(error);
    }

    // Generic 4xx/5xx toast for any error the caller doesn't explicitly silence.
    // QA #3 finding #4: Log Complaint dialog (and many others) silently swallowed
    // 403s leaving users staring at an unchanged form. This catches every
    // unhandled mutation failure with a useful message.
    //
    // Skipped automatically:
    //   - validation_failed errors (forms render fieldErrors inline instead)
    //
    // Callers that want full custom error UI can:
    //   1. Add their endpoint to SILENT_ERROR_PATHS above, OR
    //   2. Set `error.config.silent = true` before throwing, OR
    //   3. Just let this run — the global toast is informative even if the
    //      caller also shows their own inline error.
    const isValidationError = error.response?.data?.code === 'validation_failed';
    if (
      status &&
      status >= 400 &&
      !isValidationError &&
      shouldToastError(error) &&
      !error.config?.silent
    ) {
      const message = getErrorMessage(error);
      // Dedupe by MESSAGE TEXT, not by URL — if a page-level onError handler
      // is also showing toast.error('Failed to X'), this global toast and
      // the page-level toast will collapse to a single notification when
      // they happen to share text. Different messages still render
      // separately. This means the global interceptor catches everything
      // without producing duplicate toasts in the common case where the
      // page already had its own custom handler.
      toast.error(message, { id: `api-error:${message}` });
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data: { email: string; password: string; firstName?: string; lastName?: string }) =>
    api.post('/auth/register', data),

  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),

  forgotPassword: (data: { email: string }) =>
    api.post('/auth/forgot-password', data),

  resetPassword: (data: { token: string; password: string }) =>
    api.post('/auth/reset-password', data),

  verifyEmail: (token: string) =>
    api.get(`/auth/verify-email/${token}`),

  resendVerificationEmail: (data: { email: string }) =>
    api.post('/auth/resend-verification', data),

  getCurrentUser: () =>
    api.get('/users/me'),

  updateProfile: (data: { firstName?: string; lastName?: string; institutionType?: string }) =>
    api.patch('/users/me', data),
};

// Upload API
export const uploadApi = {
  uploadSingle: (
    file: File,
    teamId?: string,
    landingPageUrl?: string,
    contentCategory?: 'MARKETING' | 'PROCEDURE',
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (teamId) formData.append('teamId', teamId);
    if (landingPageUrl) formData.append('landingPageUrl', landingPageUrl);
    if (contentCategory) formData.append('contentCategory', contentCategory);

    return api.post('/uploads/single', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadBatch: (files: File[], teamId?: string, batchName?: string, landingPageUrl?: string) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (teamId) formData.append('teamId', teamId);
    if (batchName) formData.append('batchName', batchName);
    if (landingPageUrl) formData.append('landingPageUrl', landingPageUrl);

    return api.post('/uploads/batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getUploads: (params?: { teamId?: string; page?: number; limit?: number }) =>
    api.get('/uploads', { params }),

  getUpload: (id: string) =>
    api.get(`/uploads/${id}`),

  deleteUpload: (id: string) =>
    api.delete(`/uploads/${id}`),
};

// Report API
export const reportApi = {
  getReport: (id: string) =>
    api.get(`/reports/${id}`),

  getReports: (params?: { teamId?: string; status?: string; page?: number; limit?: number }) =>
    api.get('/reports', { params }),

  getSharedReport: (token: string) =>
    api.get(`/reports/shared/${token}`),

  toggleShare: (id: string, enabled: boolean) =>
    api.patch(`/reports/${id}/share`, { enabled }),

  getBatchReport: (batchId: string) =>
    api.get(`/reports/batch/${batchId}`),

  downloadReport: (id: string) =>
    api.get(`/reports/${id}/download`, { responseType: 'blob' }),

  downloadExaminerReport: (id: string, institutionName?: string) =>
    api.get(`/reports/${id}/download/examiner`, {
      responseType: 'blob',
      params: institutionName ? { institutionName } : undefined,
    }),

  downloadBulkReports: (reportIds: string[]) =>
    api.post('/reports/download/bulk', { reportIds }, { responseType: 'blob' }),

  downloadExaminerXlsx: (params?: { teamId?: string; startDate?: string; endDate?: string }) =>
    api.get('/reports/download/examiner-xlsx', { responseType: 'blob', params }),

  // Finding dismissals (Varo, May 2026)
  dismissFinding: (reportId: string, ruleId: string, data: {
    reason: string;
    promoteToExemption?: boolean;
    exemptionPattern?: string;
    detectedText?: string;
    contextSnippet?: string;
    ruleVersion?: string;
  }) => api.post(`/reports/${reportId}/findings/${ruleId}/dismiss`, data),

  undoDismissal: (reportId: string, ruleId: string, opts?: { deactivateExemption?: boolean }) =>
    api.delete(`/reports/${reportId}/findings/${ruleId}/dismiss`, {
      params: opts?.deactivateExemption ? { deactivateExemption: 'true' } : undefined,
    }),
};

// Team API
export const teamApi = {
  createTeam: (data: { name: string; description?: string }) =>
    api.post('/teams', data),

  getTeams: () =>
    api.get('/teams'),

  getTeam: (id: string) =>
    api.get(`/teams/${id}`),

  updateTeam: (id: string, data: { name?: string; description?: string }) =>
    api.patch(`/teams/${id}`, data),

  // Delete a bank/credit union. Pass { force: true } to override the
  // soft guardrail that blocks deleting a bank holding compliance data
  // (the backend returns 409 + { code: 'has_data', dataCounts } otherwise).
  deleteTeam: (id: string, opts?: { force?: boolean }) =>
    api.delete(`/teams/${id}${opts?.force ? '?force=true' : ''}`),

  inviteMember: (teamId: string, data: { email: string; role?: 'ADMIN' | 'MEMBER'; teamRole?: string }) =>
    api.post(`/teams/${teamId}/invites`, data),

  getInviteInfo: (token: string) =>
    api.get(`/teams/invites/${token}`),

  acceptInvite: (token: string) =>
    api.post(`/teams/invites/${token}/accept`),

  getTeamInvites: (teamId: string) =>
    api.get(`/teams/${teamId}/invites`),

  removeMember: (teamId: string, userId: string) =>
    api.delete(`/teams/${teamId}/members/${userId}`),

  updateMemberRole: (teamId: string, userId: string, teamRole: string) =>
    api.patch(`/teams/${teamId}/members/${userId}/role`, { teamRole }),

  getInstitutionSetup: (teamId: string) =>
    api.get(`/teams/${teamId}/institution-setup`),

  saveInstitutionSetup: (teamId: string, data: Record<string, unknown>) =>
    api.post(`/teams/${teamId}/institution-setup`, data),

  lookupByFdicCert: (cert: number) =>
    api.get(`/institution-lookup/by-fdic-cert/${cert}`),

  // FDIC BankFind peer discovery — N asset-comparable active institutions for a cert.
  findPeers: (cert: number, count = 5) =>
    api.get(`/institution-lookup/by-fdic-cert/${cert}/peers`, { params: { count } }),

  // LLM-assisted suggestion of the bank's last CRA exam date during
  // onboarding. Returns a SUGGESTION the analyst confirms before saving;
  // the wizard never auto-applies the date. (Wallace, May 2026)
  autoDetectExamDate: (teamId: string, data: {
    institutionName: string;
    fdicCertNumber?: number | null;
    state?: string | null;
    primaryRegulator?: 'FDIC' | 'OCC' | 'FED' | 'NCUA' | null;
  }) => api.post(`/teams/${teamId}/institution-setup/auto-detect-exam-date`, data),
};

// User API
export const userApi = {
  getProfile: () =>
    api.get('/users/me'),

  updateProfile: (data: { firstName?: string; lastName?: string; institutionType?: string }) =>
    api.patch('/users/me', data),

  deleteAccount: (data: { password: string; confirmation: string }) =>
    api.delete('/users/me', { data }),

  createCheckoutSession: (plan: 'PROFESSIONAL', interval: 'monthly' | 'annual' = 'monthly') =>
    api.post('/users/subscription/checkout', { plan, interval }),

  contactEnterpriseSales: (data: { companyName: string; teamSize: string; message: string }) =>
    api.post('/users/subscription/contact-enterprise', data),

  createPortalSession: () =>
    api.post('/users/subscription/portal'),
};

// Compliance Rules API
export const complianceRulesApi = {
  getVersions: () =>
    api.get('/compliance-rules/versions'),

  getVersion: (id: string) =>
    api.get(`/compliance-rules/versions/${id}`),

  getActiveVersion: () =>
    api.get('/compliance-rules/active'),

  createVersion: (data: {
    version: string;
    effectiveDate: string;
    fdicRules: any[];
    ncuaRules: any[];
    changelog?: string;
    source?: string;
    isActive?: boolean;
  }) =>
    api.post('/compliance-rules/versions', data),

  updateVersion: (id: string, data: {
    isActive?: boolean;
    changelog?: string;
    source?: string;
  }) =>
    api.patch(`/compliance-rules/versions/${id}`, data),

  activateVersion: (id: string) =>
    api.post(`/compliance-rules/versions/${id}/activate`),

  deleteVersion: (id: string) =>
    api.delete(`/compliance-rules/versions/${id}`),

  getStats: () =>
    api.get('/compliance-rules/stats'),
};

// Regulation Monitoring API
export const regulationMonitoringApi = {
  checkUpdates: (daysBack?: number) =>
    api.post('/regulation-monitoring/check', { daysBack }),

  getHistory: (params?: { page?: number; limit?: number }) =>
    api.get('/regulation-monitoring/history', { params }),

  getLatest: () =>
    api.get('/regulation-monitoring/latest'),

  getResources: () =>
    api.get('/regulation-monitoring/resources'),
};

// Regulation Review API
export const regulationReviewApi = {
  getReviews: (params?: {
    status?: string;
    agency?: string;
    page?: number;
    limit?: number;
  }) =>
    api.get('/regulation-reviews', { params }),

  getPendingCount: () =>
    api.get('/regulation-reviews/pending-count'),

  getReview: (id: string) =>
    api.get(`/regulation-reviews/${id}`),

  analyzeRegulation: (id: string) =>
    api.post(`/regulation-reviews/${id}/analyze`),

  updateReview: (id: string, data: { status?: string; reviewNotes?: string }) =>
    api.patch(`/regulation-reviews/${id}`, data),

  approve: (id: string) =>
    api.post(`/regulation-reviews/${id}/approve`),

  dismiss: (id: string, reason: string) =>
    api.post(`/regulation-reviews/${id}/dismiss`, { reason }),

  markImplemented: (id: string) =>
    api.post(`/regulation-reviews/${id}/mark-implemented`),
};

// Subscription API
export const subscriptionApi = {
  getPlan: () =>
    api.get('/subscription/plan'),

  getUsage: () =>
    api.get('/subscription/usage'),

  getFeatures: () =>
    api.get('/subscription/features'),

  requestUpgrade: (data: { targetPlan: string; message?: string }) =>
    api.post('/subscription/request-upgrade', data),

  getFoundingPartnerStatus: () =>
    api.get('/subscription/founding-partner'),
};

// Approval API
export const approvalApi = {
  submitForReview: (reportId: string, reviewerId?: string) =>
    api.post(`/approvals/${reportId}/submit`, { reviewerId }),

  bulkSubmitForReview: (reportIds: string[], reviewerId?: string, teamId?: string) =>
    api.post('/approvals/bulk-submit', { reportIds, reviewerId, teamId }),

  approve: (reportId: string, notes?: string) =>
    api.post(`/approvals/${reportId}/approve`, { notes }),

  reject: (reportId: string, notes: string) =>
    api.post(`/approvals/${reportId}/reject`, { notes }),

  returnToDraft: (reportId: string) =>
    api.post(`/approvals/${reportId}/return-to-draft`),

  nudge: (reportId: string) =>
    api.post(`/approvals/${reportId}/nudge`),

  getPendingReviews: (limit?: number) =>
    api.get('/approvals/pending', { params: { limit } }),

  getApprovalStatus: (reportId: string) =>
    api.get(`/approvals/${reportId}`),

  // Multi-step workflow (Varo, May 2026)
  pickup: (reportId: string) =>
    api.post(`/approvals/${reportId}/pickup`),

  route: (reportId: string, data: {
    targetStatus: 'UNDER_REVIEW' | 'SECOND_REVIEW' | 'LEGAL_REVIEW';
    reviewerId: string;
    note?: string;
  }) => api.post(`/approvals/${reportId}/route`, data),

  returnToBusiness: (reportId: string, reason: string) =>
    api.post(`/approvals/${reportId}/return`, { reason }),

  getStale: (params?: { teamId?: string; limit?: number }) =>
    api.get('/approvals/stale', { params }),

  getHistory: (reportId: string) =>
    api.get(`/approvals/${reportId}/history`),
};

// Notification API
export const notificationApi = {
  getNotifications: (params?: { limit?: number; offset?: number; unreadOnly?: boolean }) =>
    api.get('/notifications', { params }),

  getUnreadCount: () =>
    api.get('/notifications/unread-count'),

  markAsRead: (notificationIds: string[]) =>
    api.patch('/notifications/mark-read', { notificationIds }),

  markAllAsRead: () =>
    api.patch('/notifications/mark-all-read'),

  deleteNotifications: (notificationIds: string[]) =>
    api.delete('/notifications', { data: { notificationIds } }),
};

// Ad Metadata API
export const adMetadataApi = {
  getMetadata: (uploadId: string) =>
    api.get(`/uploads/${uploadId}/metadata`),

  updateMetadata: (uploadId: string, data: Record<string, any>) =>
    api.put(`/uploads/${uploadId}/metadata`, data),

  getTeamMetadata: (teamId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get(`/uploads/team/${teamId}/ad-metadata`, { params }),
};

// Review Comments API
export const reviewCommentApi = {
  getComments: (reportId: string) =>
    api.get(`/reports/${reportId}/comments`),

  addComment: (reportId: string, data: {
    content: string;
    violationRef?: string | null;
    sectionRef?: string | null;
    parentId?: string | null;
  }) =>
    api.post(`/reports/${reportId}/comments`, data),

  resolveComment: (reportId: string, commentId: string) =>
    api.post(`/reports/${reportId}/comments/${commentId}/resolve`),

  unresolveComment: (reportId: string, commentId: string) =>
    api.post(`/reports/${reportId}/comments/${commentId}/unresolve`),
};

// Compliance Checklist API
export const complianceChecklistApi = {
  getChecklists: (teamId: string) =>
    api.get(`/teams/${teamId}/checklists`),

  createChecklist: (teamId: string, data: {
    name: string;
    description?: string;
    items: Array<{ id: string; label: string; required: boolean; category?: string }>;
  }) =>
    api.post(`/teams/${teamId}/checklists`, data),

  updateChecklist: (teamId: string, checklistId: string, data: {
    name?: string;
    description?: string | null;
    items?: Array<{ id: string; label: string; required: boolean; category?: string }>;
    isActive?: boolean;
  }) =>
    api.put(`/teams/${teamId}/checklists/${checklistId}`, data),

  getReportCompletions: (reportId: string) =>
    api.get(`/reports/${reportId}/checklists`),

  updateCompletion: (reportId: string, checklistId: string, data: {
    completedItems: Array<{ itemId: string; checked: boolean; notes?: string }>;
  }) =>
    api.put(`/reports/${reportId}/checklists/${checklistId}`, data),
};

// Exam Package API
export const examPackageApi = {
  generatePackage: (teamId: string, data?: { startDate?: string; endDate?: string }) =>
    api.post(`/teams/${teamId}/exam-package`, data || {}, { responseType: 'blob' }),
};

// Comprehensive Exam Package (selectable, persisted) — CLARID-SPEC-PKG-001
export const packageBuilderApi = {
  createPackage: (
    teamId: string,
    data: {
      sections: string[];
      scope?: { periodStart?: string; periodEnd?: string; assessmentAreaIds?: string[] };
      outputs?: { examZip?: boolean; boardPdf?: boolean; boardDocx?: boolean };
    },
  ) => api.post(`/teams/${teamId}/packages`, data),

  listPackages: (teamId: string) => api.get(`/teams/${teamId}/packages`),

  getPackage: (teamId: string, id: string) => api.get(`/teams/${teamId}/packages/${id}`),

  getDownloadUrl: (teamId: string, id: string, artifact: 'exam' | 'board' | 'board-docx' = 'exam') =>
    api.get<{ url: string }>(`/teams/${teamId}/packages/${id}/download`, { params: { artifact } }),
};

// RBAC API
export const rbacApi = {
  getMyRole: (teamId: string) =>
    api.get(`/rbac/${teamId}/me`),

  getTeamMembers: (teamId: string) =>
    api.get(`/rbac/${teamId}/members`),

  assignRole: (teamId: string, userId: string, role: string) =>
    api.put(`/rbac/${teamId}/members/${userId}/role`, { role }),

  getRoles: (teamId: string) =>
    api.get(`/rbac/${teamId}/roles`),

  checkPermission: (teamId: string, permission: string) =>
    api.post(`/rbac/${teamId}/check-permission`, { permission }),
};

// Analytics API
export const analyticsApi = {
  getDashboard: () =>
    api.get('/analytics/dashboard'),
};

// Dashboard API
export const dashboardApi = {
  getHealth: (teamId?: string) =>
    api.get('/dashboard/health', { params: teamId ? { teamId } : undefined }),
};

// API Key Management
export const apiKeyApi = {
  createKey: (data: { name: string; teamId: string; scopes?: string[]; expiresInDays?: number }) =>
    api.post('/api-keys', data),

  listKeys: (teamId?: string) =>
    api.get('/api-keys', { params: teamId ? { teamId } : {} }),

  revokeKey: (id: string) =>
    api.delete(`/api-keys/${id}`),

  updateKey: (id: string, data: { name?: string; isActive?: boolean }) =>
    api.patch(`/api-keys/${id}`, data),
};

// MCP Compliance Checks
export const mcpChecksApi = {
  listChecks: (params?: { teamId?: string; tool?: string; status?: string; page?: number; limit?: number }) =>
    api.get('/mcp-checks', { params }),

  getCheck: (id: string) =>
    api.get(`/mcp-checks/${id}`),

  getStats: (params?: { teamId?: string; period?: string }) =>
    api.get('/mcp-checks/stats', { params }),

  reviewCheck: (id: string, reviewStatus: 'APPROVED' | 'REJECTED') =>
    api.patch(`/mcp-checks/${id}/review`, { reviewStatus }),
};

// Website Scan API
export const websiteScanApi = {
  startScan: (data: { url: string; crawlDepth?: number; maxPages?: number }) =>
    api.post('/website-scans', data),

  listScans: (params?: { page?: number; limit?: number }) =>
    api.get('/website-scans', { params }),

  getScan: (id: string) =>
    api.get(`/website-scans/${id}`),

  getScanPages: (id: string) =>
    api.get(`/website-scans/${id}/pages`),

  getPageDetail: (scanId: string, pageId: string) =>
    api.get(`/website-scans/${scanId}/pages/${pageId}`),
};

// Social Media Compliance API
export const socialMediaApi = {
  checkPost: (data: {
    text: string;
    platform?: string;
    productType?: string;
    institutionType?: string;
    teamId?: string;
  }) => api.post('/social-media/check', data),

  listChecks: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    teamId?: string;
  }) => api.get('/social-media/checks', { params }),

  getCheck: (id: string) => api.get(`/social-media/checks/${id}`),

  getStats: (params?: { teamId?: string }) =>
    api.get('/social-media/stats', { params }),
};

/** CRA Lending Test products — HMDA, SB, SF, Consumer (Types of Loan 5-8 combined), 03 and 09. Mirrors the server enum. */
export type LendingTestProduct = 'HMDA' | 'SB' | 'SF' | 'CONSUMER' | 'OTHER_SB' | 'OTHER_LOAN';

// HMDA LAR Validation API
/** Columns the LAR Validation edit table can sort by. Mirrors the server whitelist. */
export type EditSortKey =
  | 'editCode' | 'editType' | 'severity' | 'rowNumber' | 'uli'
  | 'fieldName' | 'currentValue' | 'description' | 'action';

export const hmdaApi = {
  validate: (file: File, teamId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (teamId) formData.append('teamId', teamId);
    return api.post('/hmda/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getValidations: (params?: { page?: number; limit?: number }) =>
    api.get('/hmda/validations', { params }),

  getValidation: (id: string) =>
    api.get(`/hmda/validations/${id}`),

  getEdits: (id: string, params?: {
    editType?: string;
    severity?: string;
    editCode?: string;
    rowNumber?: number;
    page?: number;
    limit?: number;
    /** Server-side, because the list is paginated — see the route. */
    sortBy?: EditSortKey;
    sortDir?: 'asc' | 'desc';
  }) =>
    api.get(`/hmda/validations/${id}/edits`, { params }),

  getEditsByLoan: (id: string) =>
    api.get(`/hmda/validations/${id}/edits/by-loan`),

  getEditsByCode: (id: string) =>
    api.get(`/hmda/validations/${id}/edits/by-code`),

  getProgress: (id: string) =>
    api.get(`/hmda/validations/${id}/edits/progress`),

  explain: (id: string) =>
    api.post(`/hmda/validations/${id}/explain`),

  exportCsv: (id: string, type: 'detail' | 'summary' = 'detail') =>
    api.get(`/hmda/validations/${id}/export/csv`, {
      params: { type },
      responseType: 'blob',
    }),

  exportPdf: (id: string) =>
    api.get(`/hmda/validations/${id}/export/pdf`, {
      responseType: 'blob',
    }),

  verifyEdit: (validationId: string, editId: string, note?: string) =>
    api.patch(`/hmda/validations/${validationId}/edits/${editId}/verify`, { note }),

  exportCorrected: (id: string) =>
    api.get(`/hmda/validations/${id}/export/corrected`, {
      responseType: 'blob',
    }),

  runDemo: (teamId?: string) =>
    api.post('/hmda/demo', teamId ? { teamId } : {}),

  deleteValidation: (id: string) =>
    api.delete(`/hmda/validations/${id}`),

  validateBatch: (files: File[], teamId?: string) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    if (teamId) formData.append('teamId', teamId);
    return api.post('/hmda/validate/batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getSuppressedEdits: (teamId: string) =>
    api.get(`/hmda/suppressed-edits`, { params: { teamId } }),

  setSuppressedEdits: (teamId: string, editCodes: string[]) =>
    api.put('/hmda/suppressed-edits', { teamId, editCodes }),
};

// ALERT / ILDR loan-trial-balance API (CLARID-SPEC-ALERT-001).
// teamId is auto-injected as a query param by the request interceptor; the upload
// passes it in the multipart body too (the interceptor doesn't touch FormData bodies).
export const alertApi = {
  upload: (file: File, opts?: { regulator?: 'FRB' | 'FDIC'; runDate?: string }, teamId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (opts?.regulator) formData.append('regulator', opts.regulator);
    if (opts?.runDate) formData.append('runDate', opts.runDate);
    if (teamId) formData.append('teamId', teamId);
    return api.post('/alert/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getFiles: (params?: { limit?: number }) =>
    api.get('/alert/files', { params }),

  getFile: (id: string) =>
    api.get(`/alert/files/${id}`),

  deleteFile: (id: string) =>
    api.delete(`/alert/files/${id}`),

  // Run SEX on primary borrowers only when co-maker is universally blank (the institution has no co-borrowers).
  setSexBorrowerOnly: (id: string, enabled: boolean) =>
    api.put(`/alert/files/${id}/sex-borrower-only`, { enabled }),

  runConsumerRateDisparity: (id: string) =>
    api.post(`/alert/files/${id}/analyze/consumer-rate-disparity`),

  getConsumerRateDisparity: (id: string) =>
    api.get(`/alert/files/${id}/analysis/consumer-rate-disparity`),

  exportAnalysis: (id: string, format: 'xlsx' | 'pdf' = 'xlsx') =>
    api.get(`/alert/files/${id}/export`, { params: { format }, responseType: 'blob' }),

  // Commercial/SB + Ag/SF volume & distribution breakdown
  getLoanDistribution: (id: string, scope?: 'all' | 'inPeriod') =>
    api.get(`/alert/files/${id}/loan-distribution`, scope ? { params: { scope } } : undefined),

  // Bank-specific code map (Collateral / Type / Note Purpose decodes)
  getCodeMaps: () => api.get('/alert/code-maps'),
  putCodeMaps: (entries: Array<{ codeType: string; code: string; decode: string }>) =>
    api.put('/alert/code-maps', { entries }),
  deleteCodeMap: (id: string) => api.delete(`/alert/code-maps/${id}`),
  importCodeMapCsv: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/alert/code-maps/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  codeMapTemplateUrl: () => `/alert/code-maps/template`,
  getUnmappedCodes: (id: string) => api.get(`/alert/files/${id}/unmapped-codes`),
};

// Compliance Feedback API
export const feedbackApi = {
  submitFeedback: (data: {
    reportId?: string;
    mcpCheckId?: string;
    flagId: string;
    feedbackType: 'INCORRECT_RULE' | 'DOES_NOT_APPLY' | 'MISSING_DISCLOSURE' | 'OTHER';
    note?: string;
  }) => api.post('/feedback', data),

  getFeedbackForReport: (reportId: string) =>
    api.get('/feedback', { params: { reportId } }),

  listFeedback: (params?: {
    status?: string;
    teamId?: string;
    page?: number;
    limit?: number;
  }) => api.get('/feedback', { params }),

  resolveFeedback: (id: string, data: {
    status: 'CONFIRMED' | 'DISMISSED';
    resolutionNote?: string;
  }) => api.patch(`/feedback/${id}`, data),
};

// Audit Log API
export const auditLogApi = {
  list: (teamId: string, params?: {
    action?: string;
    entityType?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => api.get(`/teams/${teamId}/audit-logs`, { params }),

  statistics: (teamId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get(`/teams/${teamId}/audit-logs/statistics`, { params }),

  exportCsv: (teamId: string, params?: {
    action?: string;
    entityType?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get(`/teams/${teamId}/audit-logs/export`, { params, responseType: 'blob' }),

  verifyIntegrity: (teamId: string) =>
    api.post(`/teams/${teamId}/audit-logs/verify-integrity`),
};

// Board Report API
export const boardReportApi = {
  generate: (teamId: string, data: {
    period: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' | 'CUSTOM';
    periodStart: string;
    periodEnd: string;
    title?: string;
  }) => api.post(`/teams/${teamId}/board-reports`, data),

  list: (teamId: string, params?: { limit?: number; offset?: number }) =>
    api.get(`/teams/${teamId}/board-reports`, { params }),

  get: (teamId: string, reportId: string) =>
    api.get(`/teams/${teamId}/board-reports/${reportId}`),

  downloadPdf: (teamId: string, reportId: string) =>
    api.get(`/teams/${teamId}/board-reports/${reportId}/pdf`, { responseType: 'blob' }),

  delete: (teamId: string, reportId: string) =>
    api.delete(`/teams/${teamId}/board-reports/${reportId}`),
};

// ─── Content Library API ──────────────────────────────────────────────────────

export const contentLibraryApi = {
  list: (teamId: string, params?: {
    contentStatus?: string;
    contentChannel?: string;
    contentProductType?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) => api.get(`/teams/${teamId}/content-library`, { params }),

  stats: (teamId: string) =>
    api.get(`/teams/${teamId}/content-library/stats`),

  updateMetadata: (teamId: string, reportId: string, data: {
    contentDescription?: string | null;
    contentChannel?: string | null;
    contentProductType?: string | null;
    reviewWindowDays?: number;
  }) => api.patch(`/teams/${teamId}/content-library/${reportId}/metadata`, data),

  archive: (teamId: string, reportId: string) =>
    api.patch(`/teams/${teamId}/content-library/${reportId}/archive`),

  resubmit: (teamId: string, reportId: string) =>
    api.post(`/teams/${teamId}/content-library/${reportId}/resubmit`),

  updateReviewWindow: (teamId: string, reportId: string, days: number) =>
    api.patch(`/teams/${teamId}/content-library/${reportId}/review-window`, { reviewWindowDays: days }),
};

// ─── Compliance Task / Calendar API ───────────────────────────────────────────

export const complianceTaskApi = {
  create: (teamId: string, data: {
    title: string;
    description?: string;
    category: string;
    priority?: string;
    dueDate: string;
    recurringSchedule?: string;
    reminderDays?: number;
    assignedTo?: string;
    linkedExamId?: string;
    linkedExamFindingId?: string;
    linkedReportId?: string;
  }) => api.post(`/teams/${teamId}/tasks`, data),

  list: (teamId: string, params?: {
    status?: string;
    category?: string;
    assignedTo?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get(`/teams/${teamId}/tasks`, { params }),

  upcoming: (teamId: string) =>
    api.get(`/teams/${teamId}/tasks/upcoming`),

  get: (teamId: string, taskId: string) =>
    api.get(`/teams/${teamId}/tasks/${taskId}`),

  update: (teamId: string, taskId: string, data: any) =>
    api.put(`/teams/${teamId}/tasks/${taskId}`, data),

  complete: (teamId: string, taskId: string, completionNotes?: string) =>
    api.patch(`/teams/${teamId}/tasks/${taskId}/complete`, { completionNotes }),

  cancel: (teamId: string, taskId: string) =>
    api.patch(`/teams/${teamId}/tasks/${taskId}/cancel`),

  delete: (teamId: string, taskId: string) =>
    api.delete(`/teams/${teamId}/tasks/${taskId}`),

  seedRegulatory: (teamId: string) =>
    api.post(`/teams/${teamId}/tasks/seed-regulatory`),
};

// ─── Exam Management API ──────────────────────────────────────────────────────

export const examApi = {
  create: (teamId: string, data: {
    examType: string;
    examinerAgency: string;
    scheduledStartDate?: string;
    scheduledEndDate?: string;
    leadExaminer?: string;
    leadExaminerContact?: string;
    examTeamMembers?: any[];
    focusAreas?: string[];
    notes?: string;
  }) => api.post(`/teams/${teamId}/exams`, data),

  list: (teamId: string, params?: { status?: string }) =>
    api.get(`/teams/${teamId}/exams`, { params }),

  get: (teamId: string, examId: string) =>
    api.get(`/teams/${teamId}/exams/${examId}`),

  update: (teamId: string, examId: string, data: any) =>
    api.put(`/teams/${teamId}/exams/${examId}`, data),

  updateStatus: (teamId: string, examId: string, status: string) =>
    api.patch(`/teams/${teamId}/exams/${examId}/status`, { status }),

  delete: (teamId: string, examId: string) =>
    api.delete(`/teams/${teamId}/exams/${examId}`),

  getReadiness: (teamId: string, examId: string) =>
    api.get(`/teams/${teamId}/exams/${examId}/readiness`),

  // Documents
  addDocument: (teamId: string, examId: string, data: {
    documentName: string;
    documentCategory: string;
    filePath?: string;
    fileHash?: string;
    sortOrder?: number;
    notes?: string;
  }) => api.post(`/teams/${teamId}/exams/${examId}/documents`, data),

  updateDocument: (teamId: string, examId: string, docId: string, data: any) =>
    api.put(`/teams/${teamId}/exams/${examId}/documents/${docId}`, data),

  updateDocumentStatus: (teamId: string, examId: string, docId: string, status: string) =>
    api.patch(`/teams/${teamId}/exams/${examId}/documents/${docId}/status`, { status }),

  deleteDocument: (teamId: string, examId: string, docId: string) =>
    api.delete(`/teams/${teamId}/exams/${examId}/documents/${docId}`),

  // Findings
  createFinding: (teamId: string, examId: string, data: {
    findingType: string;
    severity: string;
    category: string;
    description: string;
    regulatoryReference?: string;
    examinerComments?: string;
    remediationPlan?: string;
    remediationOwner?: string;
    remediationDeadline?: string;
    recurrence?: boolean;
    priorExamFindingId?: string;
  }) => api.post(`/teams/${teamId}/exams/${examId}/findings`, data),

  listFindings: (teamId: string, examId: string, params?: { status?: string; severity?: string }) =>
    api.get(`/teams/${teamId}/exams/${examId}/findings`, { params }),

  updateFinding: (teamId: string, examId: string, findingId: string, data: any) =>
    api.put(`/teams/${teamId}/exams/${examId}/findings/${findingId}`, data),

  updateRemediation: (teamId: string, examId: string, findingId: string, data: {
    remediationStatus: string;
    verificationNotes?: string;
  }) => api.patch(`/teams/${teamId}/exams/${examId}/findings/${findingId}/remediation`, data),

  // Requests
  createRequest: (teamId: string, examId: string, data: {
    requestedBy: string;
    description: string;
    category?: string;
    priority?: string;
    assignedTo?: string;
    dueDate?: string;
  }) => api.post(`/teams/${teamId}/exams/${examId}/requests`, data),

  listRequests: (teamId: string, examId: string) =>
    api.get(`/teams/${teamId}/exams/${examId}/requests`),

  updateRequest: (teamId: string, examId: string, reqId: string, data: any) =>
    api.put(`/teams/${teamId}/exams/${examId}/requests/${reqId}`, data),

  fulfillRequest: (teamId: string, examId: string, reqId: string, data?: {
    responseNotes?: string;
    documentIds?: string[];
  }) => api.patch(`/teams/${teamId}/exams/${examId}/requests/${reqId}/fulfill`, data || {}),
};

// ─── Fair Lending API ─────────────────────────────────────────────────────────
//
// IMPORTANT: every function in this namespace requires `teamId` as the first
// argument. The backend `/fair-lending/*` routes are team-scoped but don't
// take teamId in the URL path; the resolveTeam middleware now rejects requests
// from multi-team users that don't pass teamId explicitly (previously it
// silently fell through, causing cross-tenant data leaks — see commit history
// 2026-05-22). For GET/DELETE we pass `?teamId=...` via params; for POST/
// PUT/PATCH we add `teamId` into the body.

export const fairLendingApi = {
  runAnalysis: (teamId: string, data: {
    validationId: string;
    analysisTypes: string[];
  }) => api.post('/fair-lending/analyses', { ...data, teamId }),

  listAnalyses: (teamId: string, params?: { page?: number; limit?: number }) =>
    api.get('/fair-lending/analyses', { params: { ...params, teamId } }),

  getAnalysis: (teamId: string, id: string) =>
    api.get(`/fair-lending/analyses/${id}`, { params: { teamId } }),

  reviewFinding: (teamId: string, id: string, data: {
    status: 'OPEN' | 'ACKNOWLEDGED' | 'MITIGATED' | 'FALSE_POSITIVE';
    reviewNote: string;
  }) => api.patch(`/fair-lending/findings/${id}/review`, { ...data, teamId }),

  getDashboard: (teamId: string) =>
    api.get('/fair-lending/dashboard', { params: { teamId } }),

  deleteAnalysis: (teamId: string, id: string) =>
    api.delete(`/fair-lending/analyses/${id}`, { params: { teamId } }),

  exportPdf: (teamId: string, id: string) =>
    api.get(`/fair-lending/analyses/${id}/export/pdf`, {
      responseType: 'blob',
      params: { teamId },
    }),

  getContentFindings: (teamId: string, params?: { reportId?: string; page?: number; limit?: number; status?: string; severity?: string; category?: string }) =>
    api.get('/fair-lending/content-findings', { params: { ...params, teamId } }),

  reviewContentFinding: (teamId: string, id: string, data: { status: string; reviewNote: string }) =>
    api.patch(`/fair-lending/content-findings/${id}/review`, { ...data, teamId }),

  // Regression analysis
  runRegression: (teamId: string, data: {
    validationId: string;
    analysisType: 'DENIAL' | 'PRICING' | 'STEERING';
    triggeredBy?: string;
  }) => api.post('/fair-lending/regressions/analyze', { ...data, teamId }),

  runFullRegression: (teamId: string, data: { validationId: string }) =>
    api.post('/fair-lending/regressions/analyze/full', { ...data, teamId }),

  listRegressions: (teamId: string, params?: { page?: number; limit?: number }) =>
    api.get('/fair-lending/regressions', { params: { ...params, teamId } }),

  getRegressionEngineStatus: (teamId: string) =>
    api.get('/fair-lending/regressions/engine-status', { params: { teamId } }),

  getRegression: (teamId: string, id: string) =>
    api.get(`/fair-lending/regressions/${id}`, { params: { teamId } }),

  getRegressionFindings: (teamId: string, id: string) =>
    api.get(`/fair-lending/regressions/${id}/findings`, { params: { teamId } }),

  getRegressionMatchedPairs: (teamId: string, id: string) =>
    api.get(`/fair-lending/regressions/${id}/matched-pairs`, { params: { teamId } }),

  getRegressionTrends: (teamId: string, params?: { analysisType?: string }) =>
    api.get('/fair-lending/regressions/trends', { params: { ...params, teamId } }),

  exportRegressionPdf: (teamId: string, id: string) =>
    api.get(`/fair-lending/regressions/${id}/export/pdf`, { responseType: 'blob', params: { teamId } }),

  deleteRegression: (teamId: string, id: string) =>
    api.delete(`/fair-lending/regressions/${id}`, { params: { teamId } }),

  getRegulatorConfig: (teamId: string) =>
    api.get('/fair-lending/regulator-config', { params: { teamId } }),

  updateRegulatorConfig: (teamId: string, data: { regulatorCode: string }) =>
    api.put('/fair-lending/regulator-config', { ...data, teamId }),

  // FLD (IFLEP Analysis) analysis
  runFldAnalysis: (teamId: string, data: {
    validationId: string;
    riskFactors?: string[];
    /** Add subordinate lien Underwriting and Pricing tables. The server treats absent as true. */
    includeSubordinateLien?: boolean;
  }) => api.post('/fair-lending/fld/analyze', { ...data, teamId }),

  getFldAnalysis: (teamId: string, id: string) =>
    api.get(`/fair-lending/fld/${id}`, { params: { teamId } }),

  getFldDrillDown: (teamId: string, id: string, params: {
    riskFactor: string;
    protectedGroup: string;
    loanPurpose: string;
    lienVariant?: 'SUBORDINATE';
    openEndVariant?: 'NOT_OPEN_END' | 'OPEN_END';
    channelVariant?: 'PORTFOLIO' | 'SECONDARY_MARKET';
  }) => api.get(`/fair-lending/fld/${id}/drill-down`, { params: { ...params, teamId } }),

  exportFld: (teamId: string, id: string, format: 'xlsx' | 'docx' = 'xlsx') =>
    api.get(`/fair-lending/fld/${id}/export`, { params: { format, teamId }, responseType: 'blob' }),

  // FLRA HMDA disparity-table set (Tables 11–35) as a Word doc, for a validation.
  exportFlraHmda: (teamId: string, validationId: string) =>
    api.get(`/fair-lending/fld/flra-hmda/${validationId}/export`, { params: { teamId }, responseType: 'blob' }),

  // Partner-branded client report (CLARID-SPEC-PBR-001). Firm members only.
  // Cover-letter defaults: remembered from the last report for this firm + bank, else prefilled.
  partnerReportDefaults: (teamId: string, analysisId: string, organizationId: string) =>
    api.get<{ letter: PartnerReportLetterDefaults }>(`/fair-lending/fld/${analysisId}/partner-report/defaults`, {
      params: { organizationId, teamId },
    }),
  // The SOW checklist: what this analysis can print, and the selection last used for this firm + bank.
  partnerReportSections: (teamId: string, analysisId: string, organizationId: string) =>
    api.get<{ sections: PartnerReportSection[]; selected: string[] | null }>(
      `/fair-lending/fld/${analysisId}/partner-report/sections`,
      { params: { organizationId, teamId } },
    ),
  // Returns the .docx. X-Report-Gaps carries encodeURIComponent(JSON.stringify(string[])) of what the
  // consultant still fills in by hand. silent: errors come back as a JSON Blob, which the global toast
  // can't read — the modal parses the blob and shows the server's message itself.
  generatePartnerReport: (teamId: string, analysisId: string, body: PartnerReportRequest) =>
    api.post(`/fair-lending/fld/${analysisId}/partner-report`, body, {
      params: { teamId },
      responseType: 'blob',
      silent: true,
    } as any),

  // Branch locations (R12)
  // searchFdicInstitutions: no team scope needed (FDIC public dataset)
  searchFdicInstitutions: (query: string) =>
    api.get('/fair-lending/branches/fdic-search', { params: { q: query } }),
  importFdicBranches: (teamId: string, cert: number) =>
    api.post('/fair-lending/branches/import-fdic', { cert, teamId }),
  importCsvBranches: (teamId: string, csvContent: string) =>
    api.post('/fair-lending/branches/import-csv', { csvContent, teamId }),
  importWallaceTemplate: (teamId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('teamId', teamId);
    return api.post('/fair-lending/branches/import-wallace', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // downloadBranchTemplate: static XLSX, no team scope
  downloadBranchTemplate: () =>
    api.get('/fair-lending/branches/template.xlsx', { responseType: 'blob' }),
  deleteBranch: (teamId: string, branchId: string) =>
    api.delete(`/fair-lending/branches/${branchId}`, { params: { teamId } }),
  updateBranch: (teamId: string, branchId: string, updates: Record<string, unknown>) =>
    api.patch(`/fair-lending/branches/${branchId}`, { ...updates, teamId }),
  placeBranchOnMap: (teamId: string, branchId: string, latitude: number, longitude: number) =>
    api.post(`/fair-lending/branches/${branchId}/place`, { latitude, longitude, teamId }),
  getBranches: (teamId: string) =>
    api.get('/fair-lending/branches', { params: { teamId } }),
  clearBranches: (teamId: string) =>
    api.delete('/fair-lending/branches', { params: { teamId } }),
  validateBranchesAgainstAA: (teamId: string) =>
    api.get('/fair-lending/branches/validate-aa', { params: { teamId } }),
  getRedliningMap: (teamId: string, assessmentAreaId: string, source: 'hmda' | 'alert' = 'hmda') =>
    api.get(`/teams/${teamId}/redlining-analysis/map`, { params: { assessmentAreaId, source } }),
  // Loan-dot + branch overlays only, gated on `fair_lending` (not Enterprise
  // redlining_analysis) — for the unified /mapping page's FL-tier overlays.
  getMapOverlays: (teamId: string, assessmentAreaId: string, source: 'hmda' | 'alert' = 'hmda') =>
    api.get(`/teams/${teamId}/fair-lending/map-overlays`, { params: { assessmentAreaId, source } }),
  // Marketing distribution overlay (#3 phase 1b)
  getDistributionOverlay: (teamId: string, assessmentAreaId: string, opts?: { from?: string; to?: string }) =>
    api.get(`/teams/${teamId}/redlining-analysis/distribution-overlay`, {
      params: { assessmentAreaId, from: opts?.from, to: opts?.to },
    }),
};

// ─── Consulting firms (Organization) — membership + report branding ─────────
// CLARID-SPEC-PBR-001. Membership is explicit (OrganizationMember); most users have no firms.

export type FirmRole = 'ADMIN' | 'MEMBER';

export interface Firm {
  id: string;
  name: string;
  role: FirmRole;
  reportDisplayName: string | null;
  legalName: string | null;
  hasLogo: boolean;
  accentColor: string | null;
  addressLines: string[];
  website: string | null;
  defaultSignerName: string | null;
  defaultSignerTitle: string | null;
  libraryStatus: 'PENDING_MICHAEL_APPROVAL' | 'APPROVED';
}

// null clears a field.
export interface FirmBrandingUpdate {
  reportDisplayName?: string | null;
  legalName?: string | null;
  /** 6 hex digits, no leading # */
  accentColor?: string | null;
  /** At most 4 lines */
  addressLines?: string[];
  website?: string | null;
  defaultSignerName?: string | null;
  defaultSignerTitle?: string | null;
}

export interface PartnerReportLetterDefaults {
  recipientName: string;
  recipientTitle: string;
  institutionName: string;
  addressLines: string[];
  salutation: string;
  signerName: string;
  signerTitle: string;
  cc: string[];
}

export interface PartnerReportLetter extends PartnerReportLetterDefaults {
  date: string;
  responseDueDate: string;
  draft: boolean;
}

export interface PartnerReportRequest {
  organizationId: string;
  reportYear?: number;
  letter: PartnerReportLetter;
  /** The analyses the SOW covers; omitted = every section. */
  sections?: string[];
}

/** One analysis the client report can print (the SOW checklist). */
export interface PartnerReportSection {
  key: string;
  label: string;
  aspect: string;
}

export const firmsApi = {
  // silent: a membership probe run on every FLD dashboard visit; a failure just hides the button.
  mine: () => api.get<{ firms: Firm[] }>('/firms/mine', { silent: true } as any),

  updateBranding: (orgId: string, body: FirmBrandingUpdate) =>
    api.put<{ ok: true }>(`/firms/${orgId}/branding`, body),

  // PNG or JPEG, at most 2 MB. The server re-encodes to PNG.
  uploadLogo: (orgId: string, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post<{ ok: true }>(`/firms/${orgId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // image/png bytes; needs the auth header, so callers turn the blob into an object URL.
  // silent: a firm with no logo yet answers 404, which is not an error worth a toast.
  getLogo: (orgId: string) =>
    api.get<Blob>(`/firms/${orgId}/logo`, { responseType: 'blob', silent: true } as any),
};

// Platform-data ingests (super-admin only — see geocodingValidation.routes requireSuperAdmin).
// teamId is auto-injected by the request interceptor for /hmda/* routes. Body is {} (not null) so
// the strict body-parser accepts it.
export const adminApi = {
  ingestZctaCrosswalk: () => api.post('/hmda/geocoding/ingest-zcta-crosswalk', {}),
  ingestFfiecCensus: (year: number) => api.post('/hmda/geocoding/ingest-ffiec', { year }),
};

// Exception Tracking API
export const exceptionTrackingApi = {
  create: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/exceptions`, data),
  list: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/exceptions`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/exceptions/${id}`),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/exceptions/${id}`, data),
  delete: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/exceptions/${id}`),
  approve: (teamId: string, id: string, note?: string) =>
    api.post(`/teams/${teamId}/exceptions/${id}/approve`, { note }),
  deny: (teamId: string, id: string, reason: string) =>
    api.post(`/teams/${teamId}/exceptions/${id}/deny`, { reason }),
  getDashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/exceptions/dashboard`),
  getLORates: (teamId: string, params?: { dateFrom?: string; dateTo?: string }) =>
    api.get(`/teams/${teamId}/exceptions/analytics/lo-rates`, { params }),
  getDisparities: (teamId: string, params?: { dateFrom?: string; dateTo?: string }) =>
    api.get(`/teams/${teamId}/exceptions/analytics/disparities`, { params }),
  getTrends: (teamId: string, params?: { months?: number }) =>
    api.get(`/teams/${teamId}/exceptions/analytics/trends`, { params }),
  exportCsv: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/exceptions/export/csv`, { params, responseType: 'blob' }),
};

// HMDA Quality Monitoring API
export const hmdaQualityApi = {
  getDashboard: () =>
    api.get('/hmda/quality/dashboard'),
  getEditFailures: (params?: { dateFrom?: string; dateTo?: string }) =>
    api.get('/hmda/quality/edit-failures', { params }),
  getCompleteness: (params?: { validationId?: string }) =>
    api.get('/hmda/quality/completeness', { params }),
  getLOErrorRates: () =>
    api.get('/hmda/quality/lo-error-rates'),
  getTrends: (params?: { months?: number }) =>
    api.get('/hmda/quality/trends', { params }),
  getSystemicErrors: () =>
    api.get('/hmda/quality/systemic-errors'),
};

// Peer Benchmarking API
export const peerBenchmarkingApi = {
  getConfig: (teamId: string) =>
    api.get(`/teams/${teamId}/peer-benchmarking/config`),
  updateConfig: (teamId: string, data: any) =>
    api.put(`/teams/${teamId}/peer-benchmarking/config`, data),
  refresh: (teamId: string) =>
    api.post(`/teams/${teamId}/peer-benchmarking/refresh`),
  getDashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/peer-benchmarking/dashboard`),
  getDenialRates: (teamId: string, params?: { year?: number }) =>
    api.get(`/teams/${teamId}/peer-benchmarking/denial-rates`, { params }),
  getMarketShare: (teamId: string, params?: { year?: number }) =>
    api.get(`/teams/${teamId}/peer-benchmarking/market-share`, { params }),
  getProductMix: (teamId: string, params?: { year?: number }) =>
    api.get(`/teams/${teamId}/peer-benchmarking/product-mix`, { params }),
};

// FDIC Call Report API
export const callReportApi = {
  getConfig: (teamId: string) =>
    api.get(`/teams/${teamId}/call-reports/config`),
  updateConfig: (teamId: string, data: {
    fdicCertNumber: number;
    fdicPeerState?: string;
    fdicPeerAssetMin?: number;
    fdicPeerAssetMax?: number;
  }) =>
    api.put(`/teams/${teamId}/call-reports/config`, data),
  refresh: (teamId: string) =>
    api.post(`/teams/${teamId}/call-reports/refresh`),
  getDashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/call-reports/dashboard`),
  getTrend: (teamId: string, params?: { quarters?: number }) =>
    api.get(`/teams/${teamId}/call-reports/trend`, { params }),
};

// ─── Sprint 3: HMDA & Fair Lending Depth ─────────────────────────────────────

// Edit Intelligence API (3.1)
export const editIntelligenceApi = {
  getPatterns: (params?: { validationId?: string }) =>
    api.get('/hmda/edit-intelligence/patterns', { params }),
  getRootCauses: (params?: { validationId?: string }) =>
    api.get('/hmda/edit-intelligence/root-causes', { params }),
  getPriorityRanking: (params?: { validationId?: string }) =>
    api.get('/hmda/edit-intelligence/priority-ranking', { params }),
  getFieldMappingCheck: (params?: { validationId?: string }) =>
    api.get('/hmda/edit-intelligence/field-mapping-check', { params }),
};

// Geocoding Validation API (3.2)
export const geocodingApi = {
  ingest: (data: { year: number; records: any[] }) =>
    api.post('/hmda/geocoding/ingest', data),
  validate: (validationId: string, params?: { year?: number }) =>
    api.get(`/hmda/geocoding/validate/${validationId}`, { params }),
  lookup: (params: { tractCode: string; year?: number }) =>
    api.get('/hmda/geocoding/lookup', { params }),
  getStats: (params?: { year?: number }) =>
    api.get('/hmda/geocoding/stats', { params }),
};

// APOR Rate Spread API (3.3)
export const aporApi = {
  ingest: (data: { rates: any[] }) =>
    api.post('/hmda/apor/ingest', data),
  calculate: (params: { interestRate: number; actionDate: string; repaymentTerm: number; loanType: 'FIXED' | 'VARIABLE' }) =>
    api.get('/hmda/apor/calculate', { params }),
  validate: (validationId: string) =>
    api.get(`/hmda/apor/validate/${validationId}`),
  getRates: (params?: { actionDate?: string; loanTerm?: number }) =>
    api.get('/hmda/apor/rates', { params }),
};

// Loan Officer Monitoring API (3.4)
export const loanOfficerApi = {
  getDashboard: (source: 'hmda' | 'alert' = 'hmda') =>
    api.get('/hmda/loan-officers/dashboard', { params: { source } }),
  getLoanOfficer: (nmlsrId: string) =>
    api.get(`/hmda/loan-officers/${nmlsrId}`),
};

// Risk Assessment API (3.5)
export const riskAssessmentApi = {
  generate: (teamId: string, data: { title: string; periodStart: string; periodEnd: string }) =>
    api.post(`/teams/${teamId}/risk-assessments`, data),
  list: (teamId: string, params?: { limit?: number; offset?: number }) =>
    api.get(`/teams/${teamId}/risk-assessments`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/risk-assessments/${id}`),
  update: (teamId: string, id: string, data: { status: string }) =>
    api.put(`/teams/${teamId}/risk-assessments/${id}`, data),
  delete: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/risk-assessments/${id}`),
  downloadPdf: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/risk-assessments/${id}/pdf`, { responseType: 'blob' }),
};

// Redlining Analysis API (3.6)
export const redliningApi = {
  getOriginationMap: (teamId: string, params?: { year?: number }) =>
    api.get(`/teams/${teamId}/redlining-analysis/origination-map`, { params }),
  getMinorityComparison: (teamId: string, params?: { year?: number }) =>
    api.get(`/teams/${teamId}/redlining-analysis/minority-comparison`, { params }),
  getCraAssessment: (teamId: string, params?: { year?: number }) =>
    api.get(`/teams/${teamId}/redlining-analysis/cra-assessment`, { params }),
  getSummary: (teamId: string, params?: { year?: number }) =>
    api.get(`/teams/${teamId}/redlining-analysis/summary`, { params }),
  /** `years` = 'evaluated' asks for the years the bank has a HMDA LAR loaded for. */
  getR1Screen: (teamId: string, years: number[] | 'evaluated') =>
    api.get(`/teams/${teamId}/redlining-analysis/r1-screen`, { params: { years: years === 'evaluated' ? 'evaluated' : years.join(',') } }),
  ingestR1: (teamId: string, years: number[]) =>
    api.post(`/teams/${teamId}/redlining-analysis/r1-screen/ingest`, { years }),
  refreshR1: (teamId: string, year: number) =>
    api.post(`/teams/${teamId}/redlining-analysis/r1-screen/refresh`, { year }),
  // Standalone redlining workpaper export (R1/R9/R12) — PDF narrative or Excel tables.
  exportAnalysis: (teamId: string, format: 'pdf' | 'xlsx') =>
    api.get(`/teams/${teamId}/redlining-analysis/export`, {
      params: { format },
      responseType: 'blob',
    }),
};

// CFPB Filing API (3.7) — Local management + Login.gov OAuth + CFPB API
export const cfpbFilingApi = {
  create: (teamId: string, data: { filingYear: number; lei: string; institutionName?: string; validationId?: string }) =>
    api.post(`/teams/${teamId}/cfpb-filing`, data),
  list: (teamId: string, params?: { year?: number; limit?: number; offset?: number }) =>
    api.get(`/teams/${teamId}/cfpb-filing`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/cfpb-filing/${id}`),
  updateStatus: (teamId: string, id: string, data: { status: string; message?: string; cfpbSubmissionId?: string; totalRecords?: number; errorMessage?: string }) =>
    api.put(`/teams/${teamId}/cfpb-filing/${id}/status`, data),
  sign: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/cfpb-filing/${id}/sign`),
  delete: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/cfpb-filing/${id}`),
  // Login.gov OAuth + CFPB API integration
  authStatus: (teamId: string) =>
    api.get(`/teams/${teamId}/cfpb-filing/auth/status`),
  authLogin: (teamId: string) =>
    api.get(`/teams/${teamId}/cfpb-filing/auth/login`),
  uploadToCfpb: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/cfpb-filing/${id}/upload`),
  getEditsFromCfpb: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/cfpb-filing/${id}/edits`),
  verifyMacro: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/cfpb-filing/${id}/verify-macro`),
  submitToCfpb: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/cfpb-filing/${id}/submit`),
};

// ─── Sprint 4: Regulatory Intelligence ───────────────────────────────────────

// Regulatory Intelligence API (4.1)
export const regulatoryIntelligenceApi = {
  ingest: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/regulatory-intelligence/ingest`, data),
  list: (teamId: string, params?: { status?: string; priority?: string; agency?: string; search?: string; limit?: number; offset?: number }) =>
    api.get(`/teams/${teamId}/regulatory-intelligence`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/regulatory-intelligence/${id}`),
  review: (teamId: string, id: string, data: { reviewNotes?: string; actionItems?: string[] }) =>
    api.put(`/teams/${teamId}/regulatory-intelligence/${id}/review`, data),
  dismiss: (teamId: string, id: string, reason?: string) =>
    api.put(`/teams/${teamId}/regulatory-intelligence/${id}/dismiss`, { reason }),
  getDashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/regulatory-intelligence/dashboard`),
  getTrends: (teamId: string, params?: { months?: number }) =>
    api.get(`/teams/${teamId}/regulatory-intelligence/trends`, { params }),
};

// Enforcement Intelligence API (4.2)
export const enforcementIntelligenceApi = {
  ingest: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/enforcement-intelligence/ingest`, data),
  list: (teamId: string, params?: { actionType?: string; agency?: string; search?: string; limit?: number; offset?: number }) =>
    api.get(`/teams/${teamId}/enforcement-intelligence`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/enforcement-intelligence/${id}`),
  analyze: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/enforcement-intelligence/${id}/analyze`),
  getDashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/enforcement-intelligence/dashboard`),
  getTrends: (teamId: string, params?: { months?: number }) =>
    api.get(`/teams/${teamId}/enforcement-intelligence/trends`, { params }),
};

// State Compliance API (4.3)
export const stateComplianceApi = {
  create: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/state-compliance`, data),
  list: (teamId: string, params?: { stateCode?: string; category?: string; search?: string; limit?: number; offset?: number }) =>
    api.get(`/teams/${teamId}/state-compliance`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/state-compliance/${id}`),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/state-compliance/${id}`, data),
  delete: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/state-compliance/${id}`),
  getByState: (teamId: string, stateCode: string) =>
    api.get(`/teams/${teamId}/state-compliance/by-state/${stateCode}`),
  getSummary: (teamId: string) =>
    api.get(`/teams/${teamId}/state-compliance/summary`),
};

// Sprint 5: Policy & Procedure Management
export const policyApi = {
  create: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/policies`, data),
  list: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/policies`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/policies/${id}`),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/policies/${id}`, data),
  createVersion: (teamId: string, id: string, data: any) =>
    api.post(`/teams/${teamId}/policies/${id}/versions`, data),
  getVersion: (teamId: string, id: string, versionId: string) =>
    api.get(`/teams/${teamId}/policies/${id}/versions/${versionId}`),
  submitReview: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/policies/${id}/submit-review`),
  createReview: (teamId: string, id: string, data: any) =>
    api.post(`/teams/${teamId}/policies/${id}/reviews`, data),
  activate: (teamId: string, id: string) =>
    api.put(`/teams/${teamId}/policies/${id}/activate`),
  archive: (teamId: string, id: string) =>
    api.put(`/teams/${teamId}/policies/${id}/archive`),
  dashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/policies/dashboard`),
  gapAnalysis: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/policies/${id}/gap-analysis`),
  overdue: (teamId: string) =>
    api.get(`/teams/${teamId}/policies/overdue`),
  // ── Policy hygiene (CLARID-SPEC-BSA-001 §3.1) ─────────────────────────────
  hygiene: {
    run: (teamId: string, policyId: string) =>
      api.post(`/teams/${teamId}/policies/${policyId}/hygiene`),
    latest: (teamId: string) =>
      api.get(`/teams/${teamId}/policies/hygiene`),
    get: (teamId: string, runId: string) =>
      api.get(`/teams/${teamId}/policies/hygiene/${runId}`),
  },
  // ── Policy → marketing rules (backlog #2) ─────────────────────────────────
  previewMarketingRules: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/policies/${id}/marketing-rules/preview`),
  saveMarketingRules: (teamId: string, id: string, rules: any[]) =>
    api.post(`/teams/${teamId}/policies/${id}/marketing-rules`, { rules }),
  // ── Bulk import (CLARID-SPEC-MIG-002) ─────────────────────────────────────
  bulkImport: {
    upload: (teamId: string, files: File[]) => {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      return api.post(`/teams/${teamId}/policies/bulk-import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    getPreview: (teamId: string, previewId: string) =>
      api.get(`/teams/${teamId}/policies/bulk-import/${previewId}`),
    patchItem: (teamId: string, previewId: string, itemId: string, data: any) =>
      api.patch(`/teams/${teamId}/policies/bulk-import/${previewId}/items/${itemId}`, data),
    commit: (teamId: string, previewId: string, data: any) =>
      api.post(`/teams/${teamId}/policies/bulk-import/${previewId}/commit`, data),
    cancel: (teamId: string, previewId: string) =>
      api.delete(`/teams/${teamId}/policies/bulk-import/${previewId}`),
  },
};

// Marketing distribution mapping (backlog #3)
export const adDistributionApi = {
  list: (teamId: string, reportId: string) =>
    api.get(`/teams/${teamId}/reports/${reportId}/distributions`),
  create: (teamId: string, reportId: string, data: any) =>
    api.post(`/teams/${teamId}/reports/${reportId}/distributions`, data),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/distributions/${id}`, data),
  remove: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/distributions/${id}`),
};

// Sprint 5: Complaint Management
export const complaintApi = {
  create: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/complaints`, data),
  list: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/complaints`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/complaints/${id}`),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/complaints/${id}`, data),
  assign: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/complaints/${id}/assign`, data),
  resolve: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/complaints/${id}/resolve`, data),
  escalate: (teamId: string, id: string) =>
    api.put(`/teams/${teamId}/complaints/${id}/escalate`),
  dashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/complaints/dashboard`),
  trends: (teamId: string) =>
    api.get(`/teams/${teamId}/complaints/trends`),
  regulatorySummary: (teamId: string) =>
    api.get(`/teams/${teamId}/complaints/regulatory-summary`),
  // Sprint 8.2: Enhanced disputes & SLA
  createDispute: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/complaints/dispute`, data),
  updateStatus: (teamId: string, id: string, data: { status: string; note?: string }) =>
    api.put(`/teams/${teamId}/complaints/${id}/status`, data),
  getActivities: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/complaints/${id}/activities`),
  addActivity: (teamId: string, id: string, data: { action?: string; description: string }) =>
    api.post(`/teams/${teamId}/complaints/${id}/activities`, data),
  slaDashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/complaints/sla-dashboard`),
  recordProvisionalCredit: (teamId: string, id: string, data: { amount: number }) =>
    api.post(`/teams/${teamId}/complaints/${id}/provisional-credit`, data),

  // Intelligence layer (Varo, May 2026)
  getAnalysis: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/complaints/${id}/analysis`),
  reanalyze: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/complaints/${id}/analyze`),
  needsReview: (teamId: string, params?: { limit?: number; minRiskScore?: number }) =>
    api.get(`/teams/${teamId}/complaints/needs-review`, { params }),
  classificationMismatches: (teamId: string, params?: { limit?: number }) =>
    api.get(`/teams/${teamId}/complaints/classification-mismatches`, { params }),
  runBatchAnalysis: (teamId: string, data: {
    startDate: string;
    endDate: string;
    filters?: { status?: string; category?: string; priority?: string; channel?: string };
    analyzeMissing?: boolean;
    limit?: number;
  }) => api.post(`/teams/${teamId}/complaints/analysis/batch`, data),
  listBatches: (teamId: string, limit?: number) =>
    api.get(`/teams/${teamId}/complaints/analysis/batches`, { params: { limit } }),
  getBatch: (teamId: string, batchId: string) =>
    api.get(`/teams/${teamId}/complaints/analysis/batches/${batchId}`),
  getBatchPdf: (teamId: string, batchId: string) =>
    api.get(`/teams/${teamId}/complaints/analysis/batches/${batchId}/pdf`, {
      responseType: 'blob',
    }),
  // ── Bulk import (CLARID-SPEC-MIG-003) ─────────────────────────────────────
  bulkImport: {
    listVendors: (teamId: string) =>
      api.get(`/teams/${teamId}/complaints/bulk-import/vendors`),
    preview: (teamId: string, csv: string) =>
      api.post(`/teams/${teamId}/complaints/bulk-import/preview`, { csv }),
    updatePreview: (teamId: string, previewId: string, data: any) =>
      api.patch(`/teams/${teamId}/complaints/bulk-import/preview/${previewId}`, data),
    commit: (teamId: string, previewId: string, data: any) =>
      api.post(`/teams/${teamId}/complaints/bulk-import/${previewId}/commit`, data),
  },
};

// Exam bulk import (CLARID-SPEC-MIG-004)
export const examImportApi = {
  preview: (teamId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/teams/${teamId}/exams/bulk-import/preview`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updatePreview: (teamId: string, previewId: string, data: any) =>
    api.patch(`/teams/${teamId}/exams/bulk-import/preview/${previewId}`, data),
  commit: (teamId: string, previewId: string, data: any) =>
    api.post(`/teams/${teamId}/exams/bulk-import/${previewId}/commit`, data),
};

// Sprint 8.1: Custom Compliance Rules
export const customRulesApi = {
  list: (teamId: string, params?: { active?: boolean }) =>
    api.get(`/teams/${teamId}/custom-rules`, { params }),
  create: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/custom-rules`, data),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/custom-rules/${id}`, data),
  delete: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/custom-rules/${id}`),
  test: (teamId: string, id: string, sampleText: string) =>
    api.post(`/teams/${teamId}/custom-rules/${id}/test`, { sampleText }),
};

// Sprint 5: Vendor Compliance Portal
export const vendorComplianceApi = {
  createVendor: (teamId: string, data: { name: string; website?: string; contactEmail?: string; contactPhone?: string; category?: string; services?: string; currentRiskLevel?: string; notes?: string }) =>
    api.post(`/teams/${teamId}/vendors`, data),
  getPortal: (teamId: string, vendorId: string) =>
    api.get(`/teams/${teamId}/vendors/${vendorId}/portal`),
  inviteUser: (teamId: string, vendorId: string, data: any) =>
    api.post(`/teams/${teamId}/vendors/${vendorId}/users`, data),
  deactivateUser: (teamId: string, vendorId: string, userId: string) =>
    api.delete(`/teams/${teamId}/vendors/${vendorId}/users/${userId}`),
  createSubmission: (teamId: string, vendorId: string, data: any) =>
    api.post(`/teams/${teamId}/vendors/${vendorId}/submissions`, data),
  listSubmissions: (teamId: string, vendorId: string, params?: any) =>
    api.get(`/teams/${teamId}/vendors/${vendorId}/submissions`, { params }),
  reviewSubmission: (teamId: string, vendorId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/vendors/${vendorId}/submissions/${id}/review`, data),
  dashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/vendor-compliance/dashboard`),
  calculateRisk: (teamId: string, vendorId: string) =>
    api.post(`/teams/${teamId}/vendors/${vendorId}/risk-score`),
  expiring: (teamId: string) =>
    api.get(`/teams/${teamId}/vendor-compliance/expiring`),
  requestDocuments: (teamId: string, vendorId: string, data: any) =>
    api.post(`/teams/${teamId}/vendors/${vendorId}/request-documents`, data),
};

// ─── Sprint 6: Regulatory Knowledge Base ──────────────────────────────────

export const knowledgeBaseApi = {
  list: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/regulations`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/regulations/${id}`),
  create: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/regulations`, data),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/regulations/${id}`, data),
  delete: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/regulations/${id}`),
  addNote: (teamId: string, id: string, data: any) =>
    api.post(`/teams/${teamId}/regulations/${id}/notes`, data),
  updateNote: (teamId: string, id: string, noteId: string, data: any) =>
    api.put(`/teams/${teamId}/regulations/${id}/notes/${noteId}`, data),
  deleteNote: (teamId: string, id: string, noteId: string) =>
    api.delete(`/teams/${teamId}/regulations/${id}/notes/${noteId}`),
  dashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/regulations/dashboard`),
  seed: (teamId: string) =>
    api.post(`/teams/${teamId}/regulations/seed`),
};

// ─── Sprint 6: Product Compliance Profiles ────────────────────────────────

export const productProfileApi = {
  create: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/product-profiles`, data),
  list: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/product-profiles`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/product-profiles/${id}`),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/product-profiles/${id}`, data),
  delete: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/product-profiles/${id}`),
  mapRegulation: (teamId: string, id: string, data: any) =>
    api.post(`/teams/${teamId}/product-profiles/${id}/regulations`, data),
  unmapRegulation: (teamId: string, id: string, mappingId: string) =>
    api.delete(`/teams/${teamId}/product-profiles/${id}/regulations/${mappingId}`),
  autoMap: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/product-profiles/${id}/auto-map`),
  getChecklist: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/product-profiles/${id}/checklist`),
  addChecklistItem: (teamId: string, id: string, data: any) =>
    api.post(`/teams/${teamId}/product-profiles/${id}/checklist`, data),
  updateChecklistItem: (teamId: string, id: string, itemId: string, data: any) =>
    api.put(`/teams/${teamId}/product-profiles/${id}/checklist/${itemId}`, data),
  deleteChecklistItem: (teamId: string, id: string, itemId: string) =>
    api.delete(`/teams/${teamId}/product-profiles/${id}/checklist/${itemId}`),
  generateChecklist: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/product-profiles/${id}/generate-checklist`),
  dashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/product-profiles/dashboard`),
};

// ─── Sprint 6: Campaign Management ────────────────────────────────────────

export const campaignApi = {
  create: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/campaigns`, data),
  list: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/campaigns`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/campaigns/${id}`),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/campaigns/${id}`, data),
  delete: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/campaigns/${id}`),
  updateStatus: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/campaigns/${id}/status`, data),
  addPiece: (teamId: string, id: string, data: any) =>
    api.post(`/teams/${teamId}/campaigns/${id}/pieces`, data),
  updatePiece: (teamId: string, id: string, pieceId: string, data: any) =>
    api.put(`/teams/${teamId}/campaigns/${id}/pieces/${pieceId}`, data),
  deletePiece: (teamId: string, id: string, pieceId: string) =>
    api.delete(`/teams/${teamId}/campaigns/${id}/pieces/${pieceId}`),
  linkReport: (teamId: string, id: string, pieceId: string, data: any) =>
    api.post(`/teams/${teamId}/campaigns/${id}/pieces/${pieceId}/link-report`, data),
  complianceSummary: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/campaigns/${id}/compliance-summary`),
  calendar: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/campaigns/calendar`, { params }),
  dashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/campaigns/dashboard`),
};

// ─── Sprint 6: Channel Requirements ──────────────────────────────────────

export const channelRequirementApi = {
  list: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/channel-requirements`, { params }),
  create: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/channel-requirements`, data),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/channel-requirements/${id}`, data),
  delete: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/channel-requirements/${id}`),
  seed: (teamId: string) =>
    api.post(`/teams/${teamId}/channel-requirements/seed`),
};

// Sprint 5.2: Training Management
export const trainingApi = {
  create: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/training`, data),
  list: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/training`, { params }),
  get: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/training/${id}`),
  update: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/training/${id}`, data),
  archive: (teamId: string, id: string) =>
    api.put(`/teams/${teamId}/training/${id}/archive`),
  activate: (teamId: string, id: string) =>
    api.put(`/teams/${teamId}/training/${id}/activate`),
  dashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/training/dashboard`),
  report: (teamId: string) =>
    api.get(`/teams/${teamId}/training/report`),
  recordCompletion: (teamId: string, programId: string, data: any) =>
    api.post(`/teams/${teamId}/training/${programId}/completions`, data),
  listCompletions: (teamId: string, programId: string, params?: any) =>
    api.get(`/teams/${teamId}/training/${programId}/completions`, { params }),
  updateCompletion: (teamId: string, completionId: string, data: any) =>
    api.put(`/teams/${teamId}/training/completions/${completionId}`, data),
};

// ─── LAR Builder API ────────────────────────────────────────────────────────

export const larBuilderApi = {
  // Workspaces
  createWorkspace: (data: { name: string; filingYear: number; isPartialExempt?: boolean; exemptFieldNumbers?: number[] }) =>
    api.post('/hmda/lar-builder/workspaces', data),
  getWorkspaces: () =>
    api.get('/hmda/lar-builder/workspaces'),
  getWorkspace: (id: string) =>
    api.get(`/hmda/lar-builder/workspaces/${id}`),
  updateWorkspace: (id: string, data: any) =>
    api.patch(`/hmda/lar-builder/workspaces/${id}`, data),
  deleteWorkspace: (id: string) =>
    api.delete(`/hmda/lar-builder/workspaces/${id}`),

  // Source files
  uploadSource: (workspaceId: string, file: File, sourceSystem: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('sourceSystem', sourceSystem);
    return api.post(`/hmda/lar-builder/workspaces/${workspaceId}/sources`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getSources: (workspaceId: string) =>
    api.get(`/hmda/lar-builder/workspaces/${workspaceId}/sources`),
  previewSource: (workspaceId: string, sourceId: string) =>
    api.get(`/hmda/lar-builder/workspaces/${workspaceId}/sources/${sourceId}/preview`),
  deleteSource: (workspaceId: string, sourceId: string) =>
    api.delete(`/hmda/lar-builder/workspaces/${workspaceId}/sources/${sourceId}`),
  mapSource: (workspaceId: string, sourceId: string, templateId: string) =>
    api.post(`/hmda/lar-builder/workspaces/${workspaceId}/sources/${sourceId}/map`, { templateId }),
  autoMatchSource: (workspaceId: string, sourceId: string) =>
    api.post(`/hmda/lar-builder/workspaces/${workspaceId}/sources/${sourceId}/auto-match`),

  // Templates
  getTemplates: () =>
    api.get('/hmda/lar-builder/templates'),
  createTemplate: (data: any) =>
    api.post('/hmda/lar-builder/templates', data),
  getTemplate: (id: string) =>
    api.get(`/hmda/lar-builder/templates/${id}`),
  updateTemplate: (id: string, data: any) =>
    api.patch(`/hmda/lar-builder/templates/${id}`, data),
  deleteTemplate: (id: string) =>
    api.delete(`/hmda/lar-builder/templates/${id}`),

  // Records
  getRecords: (workspaceId: string, params?: { page?: number; limit?: number; status?: string; search?: string }) =>
    api.get(`/hmda/lar-builder/workspaces/${workspaceId}/records`, { params }),
  getRecord: (workspaceId: string, recordId: string) =>
    api.get(`/hmda/lar-builder/workspaces/${workspaceId}/records/${recordId}`),
  updateRecord: (workspaceId: string, recordId: string, data: { fields?: Record<string, string>; overrides?: any }) =>
    api.patch(`/hmda/lar-builder/workspaces/${workspaceId}/records/${recordId}`, data),

  // Operations
  mergeRecords: (workspaceId: string) =>
    api.post(`/hmda/lar-builder/workspaces/${workspaceId}/merge`),
  autoPopulate: (workspaceId: string) =>
    api.post(`/hmda/lar-builder/workspaces/${workspaceId}/auto-populate`),
  analyzeGaps: (workspaceId: string) =>
    api.post(`/hmda/lar-builder/workspaces/${workspaceId}/analyze-gaps`),
  exportLar: (workspaceId: string) =>
    api.get(`/hmda/lar-builder/workspaces/${workspaceId}/export`, { responseType: 'blob' }),
  incrementalImport: (workspaceId: string, file: File, sourceSystem: string, templateId?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('sourceSystem', sourceSystem);
    if (templateId) fd.append('templateId', templateId);
    return api.post(`/hmda/lar-builder/workspaces/${workspaceId}/incremental-import`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Phase 1B: Validation + Fair Lending
  validateLar: (workspaceId: string) =>
    api.post(`/hmda/lar-builder/workspaces/${workspaceId}/validate`),
  getValidation: (workspaceId: string) =>
    api.get(`/hmda/lar-builder/workspaces/${workspaceId}/validation`),
  runFairLending: (workspaceId: string, analysisTypes?: string[]) =>
    api.post(`/hmda/lar-builder/workspaces/${workspaceId}/fair-lending`, { analysisTypes }),
  resolveConflict: (workspaceId: string, recordId: string, fieldNumber: number, resolvedValue: string) =>
    api.post(`/hmda/lar-builder/workspaces/${workspaceId}/records/${recordId}/resolve-conflict`, { fieldNumber, resolvedValue }),

  // Phase 2: Collection Monitor
  getReadiness: (workspaceId: string) =>
    api.get(`/hmda/lar-builder/workspaces/${workspaceId}/readiness`),
  getLoGaps: (workspaceId: string) =>
    api.get(`/hmda/lar-builder/workspaces/${workspaceId}/lo-gaps`),
  sendReminders: (workspaceId: string, level?: number) =>
    api.post(`/hmda/lar-builder/workspaces/${workspaceId}/send-reminders`, { level: level || 0 }),
  getReminders: (workspaceId: string) =>
    api.get(`/hmda/lar-builder/workspaces/${workspaceId}/reminders`),
  resolveReminder: (workspaceId: string, reminderId: string) =>
    api.patch(`/hmda/lar-builder/workspaces/${workspaceId}/reminders/${reminderId}`, {}),
  importUpdate: (workspaceId: string, file: File, sourceSystem: string, templateId: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('sourceSystem', sourceSystem);
    fd.append('templateId', templateId);
    return api.post(`/hmda/lar-builder/workspaces/${workspaceId}/import-update`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Phase 3: Field Guide
  getFieldGuide: () =>
    api.get('/hmda/lar-builder/field-guide'),
  evaluateReportability: (answers: Record<string, string>) =>
    api.post('/hmda/lar-builder/reportability', { answers }),
  getChecklist: (params: { actionTaken: string; loanType?: string; loanPurpose?: string; isPartialExempt?: boolean }) =>
    api.get('/hmda/lar-builder/field-guide/checklist', { params }),
  getGmiScripts: () =>
    api.get('/hmda/lar-builder/field-guide/gmi-scripts'),
  getFieldReference: () =>
    api.get('/hmda/lar-builder/field-guide/fields'),
};

// ─── AI Performance ─────────────────────────────────────────────────────────

export const aiPerformanceApi = {
  getDashboard: (period?: string) =>
    api.get('/ai-performance/dashboard', { params: { period } }),
  getAccuracy: (period?: string) =>
    api.get('/ai-performance/accuracy', { params: { period } }),
  getAuditHealth: () =>
    api.get('/ai-performance/audit-health'),
  repairAuditChain: () =>
    api.post('/ai-performance/audit-repair'),
};

// ─── Admin System Health ────────────────────────────────────────────────────

export const adminHealthApi = {
  getSystemHealth: (period?: string) =>
    api.get('/admin/system-health', { params: { period } }),
  // Model Validation
  triggerValidation: () =>
    api.post('/admin/validation/run'),
  getValidationRuns: () =>
    api.get('/admin/validation/runs'),
  getValidationRun: (id: string) =>
    api.get(`/admin/validation/runs/${id}`),
  // Bias Testing
  triggerBiasTesting: () =>
    api.post('/admin/bias-testing/run'),
  getBiasTestRuns: () =>
    api.get('/admin/bias-testing/runs'),
  getBiasTestRun: (id: string) =>
    api.get(`/admin/bias-testing/runs/${id}`),
};

// ─── CRA Compliance API ─────────────────────────────────────────────────────

export const craApi = {
  // Config
  getConfig: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/config`),
  updateConfig: (teamId: string, data: any) =>
    api.put(`/teams/${teamId}/cra/config`, data),

  // Assessment Areas
  listAssessmentAreas: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/assessment-areas`),
  // Per-AA + overall bounding boxes derived from the team's branches.
  // Used by maps to fall back from "no points to plot" to "show the
  // bank's footprint" instead of US center.
  getAssessmentAreaBounds: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/assessment-areas/bounds`),
  createAssessmentArea: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/cra/assessment-areas`, data),
  updateAssessmentArea: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/cra/assessment-areas/${id}`, data),
  deleteAssessmentArea: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/cra/assessment-areas/${id}`),
  refreshAssessmentArea: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/cra/assessment-areas/${id}/refresh`),
  // Bulk CSV import — strict validation, upserts by (teamId, name, year).
  // Returns { inserted, updated, errors: [], saved: [...] } on success,
  // or 400 with { validationErrors: [...] } if any row fails validation.
  uploadAssessmentAreasCsv: (teamId: string, csv: string) =>
    api.post(`/teams/${teamId}/cra/assessment-areas/upload-csv`, { csv }),
  // Authenticated fetch of the CSV template; caller turns response into a
  // blob download. Cannot use a plain <a href> because the endpoint requires
  // bearer-token auth.
  getAssessmentAreasCsvTemplate: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/assessment-areas/csv-template`, { responseType: 'blob' }),
  exportAppendix: (teamId: string, format: 'xlsx' | 'docx' = 'xlsx') =>
    api.get(`/teams/${teamId}/cra/appendix/export`, { params: { format }, responseType: 'blob' }),
  getAssessmentAreaMapData: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/cra/assessment-areas/${id}/map-data`),
  getSmallBusinessDensityForAa: (teamId: string, id: string, opts?: { year?: number; includePerTract?: boolean }) =>
    // silent: true — the AA panel component handles 404 inline with a
    // friendly "no data ingested yet" message; we don't also want the
    // global error interceptor to fire a toast for the same condition.
    api.get(`/teams/${teamId}/cra/assessment-areas/${id}/small-business-density`, {
      params: { year: opts?.year, includePerTract: opts?.includePerTract ? 'true' : undefined },
      silent: true,
    } as any),
  getSmallBusinessDensityForCounty: (teamId: string, countyFips: string, year?: number) =>
    api.get(`/teams/${teamId}/cra/small-business-density/by-county/${countyFips}`, {
      params: { year },
      silent: true,
    } as any),
  getSmallBusinessDensityForTract: (teamId: string, tractCode: string, year?: number) =>
    api.get(`/teams/${teamId}/cra/small-business-density/by-tract/${tractCode}`, {
      params: { year },
      silent: true,
    } as any),
  getSmallBusinessDensityForMsa: (teamId: string, msaCode: string, year?: number) =>
    api.get(`/teams/${teamId}/cra/small-business-density/by-msa/${msaCode}`, {
      params: { year },
      silent: true,
    } as any),
  getExcludedGeography: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/cra/assessment-areas/${id}/excluded-geography`),
  getLendingConcentration: (teamId: string, id: string, validationId: string) =>
    api.get(`/teams/${teamId}/cra/assessment-areas/${id}/concentration`, { params: { validationId } }),
  msaLookup: (teamId: string, q: string, year?: number) =>
    api.get(`/teams/${teamId}/cra/msa-lookup`, { params: { q, year } }),
  getStates: (teamId: string, year?: number) =>
    api.get(`/teams/${teamId}/cra/geography/states`, { params: { year } }),
  getMsas: (teamId: string, stateCode: string, year?: number) =>
    api.get(`/teams/${teamId}/cra/geography/msas`, { params: { state: stateCode, year } }),
  getCounties: (teamId: string, stateCode: string, msaCode?: string, year?: number) =>
    api.get(`/teams/${teamId}/cra/geography/counties`, { params: { state: stateCode, msa: msaCode, year } }),
  getTracts: (teamId: string, stateCode: string, countyCode: string, year?: number) =>
    api.get(`/teams/${teamId}/cra/geography/tracts`, { params: { state: stateCode, county: countyCode, year } }),
  getAvailableYears: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/geography/available-years`),

  // Lending Analysis
  runLendingAnalysis: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/cra/lending-analysis`, data),
  listLendingAnalyses: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/cra/lending-analysis`, { params }),
  getLatestLendingAnalysis: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/lending-analysis/latest`),
  getLendingAnalysis: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/cra/lending-analysis/${id}`),
  deleteLendingAnalysis: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/cra/lending-analysis/${id}`),
  getHmdaLendingTest: (teamId: string, params: { validationId: string; analysisYear: number }) =>
    api.get(`/teams/${teamId}/cra/lending-test/hmda`, { params }),
  // Multi-year × multi-product AA-Concentration matrix (PE format).
  getConcentrationMatrix: (teamId: string, items: Array<{ product: LendingTestProduct; year: number; validationId?: string }>) =>
    api.post(`/teams/${teamId}/cra/lending-test/concentration-matrix`, { items }),
  // Multi-year GD + BPD, stacked per AA (PE format).
  getMultiYearLendingTest: (teamId: string, selections: Array<{ year: number; validationId: string }>) =>
    api.post(`/teams/${teamId}/cra/lending-test/multi-year`, { selections }),
  // Unified one-call Lending Test: AA-Concentration matrix + multi-year GD/BPD from one selection.
  getFullLendingTest: (teamId: string, items: Array<{ product: LendingTestProduct; year: number; validationId?: string }>) =>
    api.post(`/teams/${teamId}/cra/lending-test/full`, { items }),
  // Export to Excel (.xlsx) — same selection as getFullLendingTest; response is a blob.
  exportLendingTestExcel: (teamId: string, items: Array<{ product: LendingTestProduct; year: number; validationId?: string }>) =>
    api.post(`/teams/${teamId}/cra/lending-test/export-excel`, { items }, { responseType: 'blob' }),
  // Export to Word (.docx) — same selection as getFullLendingTest; response is a blob.
  // includeSourceNote mirrors the on-screen "Show Source / Note" toggle; off by default.
  exportLendingTestWord: (teamId: string, items: Array<{ product: LendingTestProduct; year: number; validationId?: string }>, includeSourceNote = false) =>
    api.post(`/teams/${teamId}/cra/lending-test/export-word`, { items, includeSourceNote }, { responseType: 'blob' }),
  // Auto-pull the HMDA market aggregate (Geographic Distribution) from the public CFPB Data
  // Browser for every AA + year. persist:false = preview only (reconcile); true = save it.
  pullHmdaAggregate: (teamId: string, year: number, persist: boolean) =>
    // Body must be {} not null: the axios instance defaults Content-Type to application/json,
    // so a null body serializes to the literal string "null", which Express's strict body-parser
    // rejects with "Unexpected token 'n', \"null\" is not valid JSON". The payload rides in params.
    api.post(`/teams/${teamId}/cra/hmda-aggregate/pull`, {}, { params: { year, persist } }),
  // Which HMDA years currently have a SAVED market aggregate (per-year AA coverage + loan counts).
  getHmdaAggregateStatus: (teamId: string) =>
    api.get<{ years: Array<{ dataYear: number; assessmentAreaCount: number; totalLoans: number }> }>(
      `/teams/${teamId}/cra/hmda-aggregate/status`,
    ),
  getAggregateTables: (teamId: string, params?: { validationId?: string; assessmentAreaId?: string }) =>
    api.get(`/teams/${teamId}/cra/lending-analysis/aggregate-tables`, { params }),

  // CD Activities
  listCdActivities: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/cra/cd-activities`, { params }),
  createCdActivity: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/cra/cd-activities`, data),
  updateCdActivity: (teamId: string, id: string, data: any) =>
    api.put(`/teams/${teamId}/cra/cd-activities/${id}`, data),
  deleteCdActivity: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/cra/cd-activities/${id}`),
  importCdActivities: (teamId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/teams/${teamId}/cra/cd-activities/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getCdSummary: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/cd-summary`),

  // Gap Analysis
  runGapAnalysis: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/cra/gap-analysis`, data),
  listGapAnalyses: (teamId: string, params?: any) =>
    api.get(`/teams/${teamId}/cra/gap-analysis`, { params }),
  getLatestGapAnalysis: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/gap-analysis/latest`),
  getGapAnalysis: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/cra/gap-analysis/${id}`),

  // Dashboard
  getDashboard: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/dashboard`),
  getCrossModule: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/dashboard/cross-module`),

  // Exam Readiness
  getExamReadiness: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/exam-readiness`),

  // Dashboard actions
  seedCraTasks: (teamId: string) =>
    api.post(`/teams/${teamId}/cra/dashboard/seed-tasks`),

  // Public Files
  listPublicFiles: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/public-files`),
  createPublicFile: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/cra/public-files`, data),
  deletePublicFile: (teamId: string, id: string) =>
    api.delete(`/teams/${teamId}/cra/public-files/${id}`),
  generateNotice: (teamId: string, year?: number) =>
    api.post(`/teams/${teamId}/cra/public-files/generate-notice`, { year: year || new Date().getFullYear() }),

  // CRAD (CRA Dashboard)
  importSbLoans: (teamId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/teams/${teamId}/cra/sb-loans/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listSbLoans: (teamId: string, year?: number) =>
    api.get(`/teams/${teamId}/cra/sb-loans`, { params: year ? { year } : {} }),
  deleteSbLoans: (teamId: string, year?: number) =>
    api.delete(`/teams/${teamId}/cra/sb-loans`, { params: year ? { year } : {} }),
  createCrad: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/cra/crad`, data),
  listCrads: (teamId: string) =>
    api.get(`/teams/${teamId}/cra/crad`),
  getCrad: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/cra/crad/${id}`),
  /** Per-AA delineation + FFIEC demographic table (same rows as the CRA Appendix export). */
  getCradAssessmentAreas: (teamId: string, id: string) =>
    api.get(`/teams/${teamId}/cra/crad/${id}/assessment-areas`),
  computeCrad: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/cra/crad/${id}/compute`),
  upsertComparator: (teamId: string, data: any) =>
    api.post(`/teams/${teamId}/cra/crad/comparators`, data),
  generateMemorandum: (teamId: string, id: string) =>
    api.post(`/teams/${teamId}/cra/crad/${id}/memorandum`),
};

// ─── CRAD FDIC Large Bank (CLARID-SPEC-CRAD-LARGE-001) ──────────────────────
export const cradLargeBankApi = {
  list: (teamId: string) => api.get(`/teams/${teamId}/cra/large-bank`),
  get: (teamId: string, reportId: string) =>
    api.get(`/teams/${teamId}/cra/large-bank/${reportId}`),
  create: (
    teamId: string,
    data: {
      bankName: string;
      startDate: string;
      endDate: string;
      priorExamDate?: string;
      fdicCertNumber?: number;
      primaryRegulator?: string;
    },
  ) => api.post(`/teams/${teamId}/cra/large-bank`, data),
  update: (teamId: string, reportId: string, data: Record<string, unknown>) =>
    api.patch(`/teams/${teamId}/cra/large-bank/${reportId}`, data),
  compute: (teamId: string, reportId: string) =>
    api.post(`/teams/${teamId}/cra/large-bank/${reportId}/compute`),
  computeInvestmentTest: (
    teamId: string,
    reportId: string,
    body: { avgTotalAssets?: number; avgTotalSecurities?: number; reviewYear?: number } = {},
  ) =>
    api.post(`/teams/${teamId}/cra/large-bank/${reportId}/compute-investment-test`, body),
  computeServiceTest: (teamId: string, reportId: string, body: { reviewYear?: number } = {}) =>
    api.post(`/teams/${teamId}/cra/large-bank/${reportId}/compute-service-test`, body),
  computeLendingTest: (
    teamId: string,
    reportId: string,
    body: {
      depositsByAa: Array<{ assessmentAreaId: string; bankDeposits: number }>;
      avgTotalAssets?: number;
      avgTotalLoans?: number;
      loanYears?: number[];
      reviewYear?: number;
    },
  ) => api.post(`/teams/${teamId}/cra/large-bank/${reportId}/compute-lending-test`, body),
  extractIf: (teamId: string, reportId: string) =>
    api.post(`/teams/${teamId}/cra/large-bank/${reportId}/extract-if`),
  listIfSuggestions: (teamId: string, reportId: string) =>
    api.get(`/teams/${teamId}/cra/large-bank/${reportId}/if-suggestions`),
  updateIfSuggestion: (
    teamId: string,
    reportId: string,
    suggestionId: string,
    body: {
      status?: 'PENDING' | 'ACCEPTED' | 'EDITED' | 'REJECTED';
      editedName?: string;
      editedRationale?: string;
      analystNotes?: string;
      proposedRating?: string;
    },
  ) =>
    api.patch(
      `/teams/${teamId}/cra/large-bank/${reportId}/if-suggestions/${suggestionId}`,
      body,
    ),
  deleteIfSuggestion: (teamId: string, reportId: string, suggestionId: string) =>
    api.delete(`/teams/${teamId}/cra/large-bank/${reportId}/if-suggestions/${suggestionId}`),

  generateMemorandum: (
    teamId: string,
    reportId: string,
    body: { enrichWithAi?: boolean } = {},
  ) => api.post(`/teams/${teamId}/cra/large-bank/${reportId}/generate-memorandum`, body),
  memorandumMarkdownUrl: (teamId: string, reportId: string) =>
    `${api.defaults.baseURL ?? ''}/teams/${teamId}/cra/large-bank/${reportId}/memorandum.md`,
  memorandumPdfUrl: (teamId: string, reportId: string) =>
    `${api.defaults.baseURL ?? ''}/teams/${teamId}/cra/large-bank/${reportId}/memorandum.pdf`,

  updateTestResult: (
    teamId: string,
    reportId: string,
    resultId: string,
    body: { rating?: string | null; conclusion?: string; reviewScope?: string },
  ) =>
    api.patch(
      `/teams/${teamId}/cra/large-bank/${reportId}/test-results/${resultId}`,
      body,
    ),

  updateStateRating: (
    teamId: string,
    reportId: string,
    stateRatingId: string,
    body: {
      stateName?: string;
      isMultistateMSA?: boolean;
      msaCode?: string | null;
      lendingTestRating?: string | null;
      investmentTestRating?: string | null;
      serviceTestRating?: string | null;
      totalStateDeposits?: number | null;
      totalStateLoans?: number | null;
      stateDepositSharePct?: number | null;
      notes?: string | null;
    },
  ) =>
    api.patch(
      `/teams/${teamId}/cra/large-bank/${reportId}/state-ratings/${stateRatingId}`,
      body,
    ),

  addStateRating: (
    teamId: string,
    reportId: string,
    body: { stateName: string; isMultistateMSA?: boolean; msaCode?: string },
  ) => api.post(`/teams/${teamId}/cra/large-bank/${reportId}/state-ratings`, body),

  deleteStateRating: (teamId: string, reportId: string, stateRatingId: string) =>
    api.delete(`/teams/${teamId}/cra/large-bank/${reportId}/state-ratings/${stateRatingId}`),

  recomputeStateRollup: (teamId: string, reportId: string) =>
    api.post(`/teams/${teamId}/cra/large-bank/${reportId}/recompute-state-rollup`),
  remove: (teamId: string, reportId: string) =>
    api.delete(`/teams/${teamId}/cra/large-bank/${reportId}`),

  addRecommendation: (teamId: string, reportId: string, text: string, sequence?: number) =>
    api.post(`/teams/${teamId}/cra/large-bank/${reportId}/recommendations`, { text, sequence }),
  removeRecommendation: (teamId: string, reportId: string, recId: string) =>
    api.delete(`/teams/${teamId}/cra/large-bank/${reportId}/recommendations/${recId}`),

  addPeerBenchmark: (
    teamId: string,
    reportId: string,
    data: {
      peerName: string;
      peerCertNumber?: number;
      peerExamDate?: string;
      peerAssetSize?: number;
      cdLoansSummary?: string;
      qualifiedInvestmentsSummary?: string;
      cdServicesSummary?: string;
      overallConclusion?: string;
    },
  ) => api.post(`/teams/${teamId}/cra/large-bank/${reportId}/peer-benchmarks`, data),
  removePeerBenchmark: (teamId: string, reportId: string, peerId: string) =>
    api.delete(`/teams/${teamId}/cra/large-bank/${reportId}/peer-benchmarks/${peerId}`),

  workbookExportUrl: (teamId: string, reportId: string) =>
    `${api.defaults.baseURL ?? ''}/teams/${teamId}/cra/large-bank/${reportId}/workbook`,
};

// ─── Bond Analysis (RJ partnership) ─────────────────────────────────────────
export const bondsApi = {
  /** Look up a single CUSIP joined with the team's CRA qualification. */
  getBond: (teamId: string, cusip: string) =>
    api.get(`/teams/${teamId}/bonds/${encodeURIComponent(cusip)}`),

  /**
   * Bulk CUSIP lookup. Accepts up to 500 CUSIPs and an optional bank-context
   * teamId (defaults to the team that owns the lookup). Synchronous compute;
   * runs full ingest+qualify pipeline per CUSIP. Response includes per-CUSIP
   * status so partial failures still return useful data.
   */
  bulkLookup: (
    teamId: string,
    payload: { cusips: string[]; bankContextTeamId?: string; label?: string },
  ) => api.post(`/teams/${teamId}/bonds/lookup`, payload),

  /** Force a recompute of the CRA qualification for one CUSIP × team. */
  qualifyCusip: (teamId: string, cusip: string) =>
    api.post(`/teams/${teamId}/bonds/${encodeURIComponent(cusip)}/qualify`),

  /**
   * Register a CUSIP → Deal mapping and auto-trigger OCS extraction. The
   * MSIA Property Detail CSV doesn't carry CUSIPs, so this is the analyst's
   * path to wire up a CUSIP that isn't already in the system. For
   * WN-series, the OCS PDF gets pulled and parsed in the same call — no
   * separate upload needed.
   */
  registerBond: (
    teamId: string,
    payload: { cusip: string; dealId: string; agency?: string; seriesPrefix?: string },
  ) => api.post(`/teams/${teamId}/bonds/register`, payload),

  /** Upload an MSIA Property Detail CSV export (Freddie). RJ analyst workflow. */
  uploadMsiaCsv: (teamId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/teams/${teamId}/bonds/upload-msia-csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** Upload a Fannie DUS Disclose Detail Report xlsx. RJ analyst workflow. */
  uploadFannieXlsx: (teamId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/teams/${teamId}/bonds/upload-fannie-xlsx`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** Trigger OCS PDF extraction for one Freddie deal. */
  extractOcsForDeal: (teamId: string, dealId: string) =>
    api.post(`/teams/${teamId}/bonds/deals/${encodeURIComponent(dealId)}/extract-ocs`),

  /** List recent lookup requests (for the audit / "recent" sidebar). */
  listLookups: (teamId: string, limit?: number) =>
    api.get(`/teams/${teamId}/bonds/lookups`, { params: limit ? { limit } : {} }),

  /** Reload the result payload from one past lookup (no recompute). */
  getLookup: (teamId: string, lookupId: string) =>
    api.get(`/teams/${teamId}/bonds/lookups/${lookupId}`),

  /** List the team's computed CRA qualifications across all known bonds. */
  listQualifications: (teamId: string) =>
    api.get(`/teams/${teamId}/bonds/qualifications`),

  /**
   * Inventory view of every Deal in the system. Supports search across
   * dealId / dealName / underlying CUSIP, plus agency and extraction-status
   * filters. Paginated.
   */
  listLibrary: (
    teamId: string,
    params?: { search?: string; agency?: string; status?: string; limit?: number; offset?: number },
  ) => api.get(`/teams/${teamId}/bonds/library`, { params }),

  /** Full detail for one Deal: bonds, loans, properties, affordability, qualifications. */
  getLibraryDeal: (teamId: string, dealId: string) =>
    api.get(`/teams/${teamId}/bonds/library/${encodeURIComponent(dealId)}`),

  /**
   * Generate the analyst pitch PDF (cover page roll-up + per-bond detail).
   * Returns a Blob; caller is responsible for triggering the download.
   */
  pitchPdf: (
    teamId: string,
    payload: { cusips: string[]; bankName?: string; analystName?: string; bankContextTeamId?: string },
  ) =>
    api.post(`/teams/${teamId}/bonds/pitch-pdf`, payload, { responseType: 'blob' }),

  /** Upload an MBS single-family CRA-targeted pool xlsx (keyed by offering). */
  uploadSfPool: (teamId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/teams/${teamId}/bonds/upload-sf-pool`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Generate the independent CRA validation report for one single-family pool ×
   * one bank. Returns a Blob; caller triggers the download.
   */
  sfValidationPdf: (
    teamId: string,
    payload: {
      cusip: string;
      bankName?: string;
      bankContextTeamId?: string;
      poolMeta?: { securityType?: string | null; securityRate?: number | null; state?: string | null; qualificationBasis?: string | null };
      // Engagement details for the client-facing header + ad-hoc assessment area.
      primaryRegulator?: string;
      institutionCityState?: string;
      fdicCert?: string;
      contact?: string;
      validationRef?: string;
      preparedBy?: string;
      assessmentArea?: { name?: string; countyCodes?: string[]; tractCodes?: string[] };
    },
  ) => api.post(`/teams/${teamId}/bonds/sf-validation-pdf`, payload, { responseType: 'blob' }),

  // Look up an institution + branch-county footprint by FDIC cert.
  fdicLookup: (teamId: string, cert: string) =>
    api.get(`/teams/${teamId}/bonds/fdic-lookup/${encodeURIComponent(cert)}`),

  // Geography lookups for the validation-screen AA picker (module_bonds gated).
  geographyStates: (teamId: string, year?: number) =>
    api.get(`/teams/${teamId}/bonds/geography/states`, { params: { year } }),
  geographyMsas: (teamId: string, stateCode: string, year?: number) =>
    api.get(`/teams/${teamId}/bonds/geography/msas`, { params: { state: stateCode, year } }),
  geographyCounties: (teamId: string, stateCode: string, msaCode?: string, year?: number) =>
    api.get(`/teams/${teamId}/bonds/geography/counties`, { params: { state: stateCode, msa: msaCode, year } }),
};

// ── BSA/AML obligation register (CLARID-SPEC-BSA-001 §3.2) ──────────────────
export const bsaApi = {
  obligations: {
    list: (teamId: string) => api.get(`/teams/${teamId}/bsa/obligations`),
    bootstrap: (teamId: string, defaultRiskTier?: string | null) =>
      api.post(`/teams/${teamId}/bsa/obligations/bootstrap`, { defaultRiskTier }),
    update: (teamId: string, id: string, data: Record<string, unknown>) =>
      api.patch(`/teams/${teamId}/bsa/obligations/${id}`, data),
    complete: (teamId: string, id: string, data: Record<string, unknown>) =>
      api.post(`/teams/${teamId}/bsa/obligations/${id}/complete`, data),
    project: (teamId: string, horizonDays?: number) =>
      api.post(`/teams/${teamId}/bsa/obligations/project`, { horizonDays }),
  },
  assessments: {
    list: (teamId: string) => api.get(`/teams/${teamId}/bsa/assessments`),
    create: (teamId: string, data: Record<string, unknown>) =>
      api.post(`/teams/${teamId}/bsa/assessments`, data),
    get: (teamId: string, id: string) => api.get(`/teams/${teamId}/bsa/assessments/${id}`),
    finalize: (teamId: string, id: string) =>
      api.post(`/teams/${teamId}/bsa/assessments/${id}/finalize`),
  },
  factors: {
    update: (teamId: string, id: string, data: Record<string, unknown>) =>
      api.patch(`/teams/${teamId}/bsa/factors/${id}`, data),
  },
  diagnostics: {
    flatCadence: (teamId: string) =>
      api.get(`/teams/${teamId}/bsa/diagnostics/flat-cadence`),
  },
};
